// ─────────────────────────────────────────────
//  ProfileRewards.jsx
//  Connected to: ProfileLogic.js, Profile.css
//  Used by: Profile.jsx
// ─────────────────────────────────────────────

import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "./Profile";
import {
  REDEMPTION_STATUS,
  MILESTONES,
  MILESTONE_REWARD_RATES,
  MIN_SVC_FEE_REDEMPTION,
  WELCOME_BONUS,
  COMMISSION_RATES,
  REFERRAL_DISCOUNT_RATES,
  formatDate,
  getDocFileData,
  deletePayoutRequest,
} from "./ProfileLogic.js";
import { getShopOrders } from "./Auth.js";
import RedemptionService from "../../../services/redemptionService";
import MeetingService from "../../../services/meetingService";
import PassportService from "../../../services/passportService";

import receiptLogo from "../../../assets/PNG/RecieptLogo.png";
import { generateEarningReportHTML } from "../../../report/reportConstants";

// ── Custom amount rounding: fractional <0.5 → same number, >=0.5 → next integer ──
const roundAmount = (amount) => {
  const num = Number(amount);
  if (isNaN(num)) return 0;
  const fractional = num % 1;
  if (fractional >= 0.5) return Math.ceil(num);
  return Math.floor(num);
};

// ────────────────────────────────────────────
//  RECEIPT PDF HELPERS
// ────────────────────────────────────────────
function openReceiptWindow(html) {
  const w = window.open("", "_blank", "width=720,height=960,scrollbars=yes");
  if (!w) { alert("Please allow pop-ups to download the receipt as PDF"); return; }
  w.document.write(html);
  w.document.close();
}

function receiptStyle() {
  return `*{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;color:#1a1a2e;max-width:620px;margin:40px auto;padding:0 20px}
  .hdr{text-align:center;padding-bottom:18px;border-bottom:2px solid #0d2156;margin-bottom:22px}
  .logo{font-size:22px;font-weight:800;color:#0d2156}
  .sub{font-size:13px;color:#666;margin-top:4px}
  .badge{display:inline-block;padding:3px 12px;border-radius:20px;font-size:11px;font-weight:700;margin-top:8px}
  .b-green{background:#dcfce7;color:#16a34a;border:1px solid #86efac}
  .b-blue{background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd}
  .b-amber{background:#fef3c7;color:#d97706;border:1px solid #fcd34d}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;margin-bottom:20px;font-size:13px}
  .lbl{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#888;margin-bottom:2px}
  .val{font-weight:600;color:#1a1a2e}
  .full{grid-column:1/-1}
  .totals{border-top:2px solid #0d2156;padding-top:14px;margin-top:8px}
  .trow{display:flex;justify-content:space-between;font-size:13px;margin-bottom:7px}
  .grand{font-size:16px;font-weight:800;color:#0d2156;border-top:1px dashed #ccc;padding-top:10px;margin-top:4px}
  .ftr{text-align:center;margin-top:28px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:14px;line-height:1.8}
  @media print{body{margin:10px}}`;
}

function downloadPayoutReceipt(request) {
  const dateStr = request.submittedAt
    ? new Date(request.submittedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "N/A";

  const hasNewCheque = !!request.chequeDataUrl;
  const hasSavedCheque = !!request.savedChequeUrl;

  const uploadLabel = hasNewCheque ? "NEW"
    : hasSavedCheque ? "SAVED"
    : "\u2014";

  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Payout Receipt — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">Payout Receipt</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            ${request.bankName ? `<div class="report-info-title">Bank</div><div class="report-info-value">${request.bankName}${request.accountNo ? " \u2014 " + request.accountNo : ""}</div>` : ""}
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">DATE</th>
            <th class="report-th">REQUEST ID</th>
            <th class="report-th">ACCOUNT No.</th>
            <th class="report-th">VOID CHEQUE</th>
            <th class="report-th">AMOUNT</th>
          </tr>
        </thead>
        <tbody class="report-tbody">
          <tr>
            <td class="report-td">${dateStr}</td>
            <td class="report-td">${request.id || "N/A"}</td>
            <td class="report-td">${request.bankDetails?.accountNumber || request.accountNo || "\u2014"}</td>
            <td class="report-td">${uploadLabel}</td>
            <td class="report-td">₹${roundAmount(request.amount || 0).toLocaleString("en-IN")}</td>
          </tr>
          <tr>
            <td class="report-total-td" colspan="4" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(request.amount || 0).toLocaleString("en-IN")}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

function downloadBillReceipt(request) {
  const dateStr = request.submittedAt
    ? new Date(request.submittedAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
    : "N/A";
  openReceiptWindow(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Bill Upload Receipt</title>
    <style>${receiptStyle()}</style></head><body>
    <div class="hdr"><div class="logo">&#127758; TheVisaCo</div><div class="sub">Bill Upload Receipt</div>
    <span class="badge b-amber">🧲 Bill Upload</span></div>
    <div class="grid">
      <div><div class="lbl">Ref ID</div><div class="val">${request.id || "N/A"}</div></div>
      <div><div class="lbl">Date</div><div class="val">${dateStr}</div></div>
      <div><div class="lbl">Category</div><div class="val">${request.category || "N/A"}</div></div>
      <div><div class="lbl">Amount</div><div class="val">₹${(request.amount || 0).toLocaleString("en-IN")}</div></div>
      <div><div class="lbl">Status</div><div class="val">${request.status || "Pending"}</div></div>
      ${request.offerName ? `<div class="full"><div class="lbl">Offer</div><div class="val">${request.offerName}</div></div>` : ""}
    </div>
    <div class="ftr"><p>TheVisaCo — Bill upload cashback credited within 3–5 business days.</p><p>support@thevisaco.com</p></div>
    <script>window.onload=function(){window.print();}</script></body></html>`);
}

function downloadShopOrderReceipt(order) {
  const isPoints = order.paymentMethod === "Points";
  const dateStr  = order.date
    ? new Date(order.date).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })
    : order.date || "N/A";
  const itemRows = (order.items || []).map((it) =>
    `<tr><td style="padding:7px 6px;border-bottom:1px solid #eef0f4">${it.name}</td>
     <td style="padding:7px 6px;border-bottom:1px solid #eef0f4;text-align:center">${it.qty}</td>
     <td style="padding:7px 6px;border-bottom:1px solid #eef0f4;text-align:right">${isPoints ? "—" : `₹${(it.price * it.qty).toLocaleString("en-IN")}`}</td></tr>`
  ).join("");
  openReceiptWindow(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Shopping Receipt</title>
    <style>${receiptStyle()}table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px}thead tr{background:#f5f7fa}th{padding:7px 6px;text-align:left;font-size:11px;text-transform:uppercase;color:#555;letter-spacing:.05em}</style>
    </head><body>
    <div class="hdr"><div class="logo">&#127758; TheVisaCo Shop</div><div class="sub">Shopping Receipt</div>
    <span class="badge ${isPoints ? "b-amber" : "b-green"}">${isPoints ? "⭐ Points Order" : "✓ Paid Order"}</span></div>
    <div class="grid">
      <div><div class="lbl">Order ID</div><div class="val">${order.id}</div></div>
      <div><div class="lbl">Date</div><div class="val">${dateStr}</div></div>
      <div><div class="lbl">Tracking ID</div><div class="val">${order.trackingId || "N/A"}</div></div>
      <div><div class="lbl">Payment</div><div class="val">${order.paymentMethod}</div></div>
    </div>
    <table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>${itemRows}</tbody></table>
    <div class="totals">
      ${isPoints
        ? `<div class="trow grand"><span>⭐ Points Used</span><span>${(order.pointsUsed || 0).toLocaleString("en-IN")} pts</span></div>
           <div class="trow"><span style="font-size:11px;color:#888">Value (5 pts = ₹1)</span><span style="font-size:11px;color:#888">₹${((order.pointsUsed || 0) / 5).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>`
        : `<div class="trow grand"><span>Total Paid</span><span>₹${roundAmount(order.total || 0).toLocaleString("en-IN")}</span></div>`
      }
    </div>
    <div class="ftr"><p>Thank you for shopping at TheVisaCo!</p><p>support@thevisaco.com</p></div>
    <script>window.onload=function(){window.print();}</script></body></html>`);
}

function generateOrderPdfReport(orders, userEmail, reportLabel) {
  if (!orders || orders.length === 0) return;

  const rows = orders
    .map((o) => {
      const items = o.items?.map((it) => `${it.name} x${it.qty}`).join(", ") || o.productName || "N/A";
      const isPoints = o.paymentMethod === "Points";
      return `
    <tr>
      <td class="report-td">${o.date ? new Date(o.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${o.id || o.orderId || o.order_id || "N/A"}</td>
      <td class="report-td">${items}</td>
      <td class="report-td">${o.quantity || 1}</td>
      <td class="report-td">${isPoints ? "Points" : (o.paymentMethod || "Paid")}</td>
      <td class="report-td">${o.country || "\u2014"}</td>
      <td class="report-td">₹${roundAmount(o.total || o.amount || 0).toLocaleString("en-IN")}</td>
      <td class="report-td" style="color:#dc2626">₹${(o.rewardUsed || 0).toLocaleString("en-IN")}</td>
    </tr>`;
    })
    .join("");

  const total = orders.reduce((sum, o) => sum + (o.total || o.amount || 0), 0);
  const totalReward = orders.reduce((sum, o) => sum + (o.rewardUsed || 0), 0);
  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${reportLabel} — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
   .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-total-card{width:min(100%,248px);margin-left:auto;margin-top:16px;background:rgba(255,255,255,0.52);border-radius:26px;padding:18px;box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-total-row{display:flex;justify-content:space-between;margin-bottom:10px;font-size:14px;color:#000000;font-weight:300;text-align:center;}
  .report-grand-total{margin-top:12px;padding-top:12px;border-top:1px solid rgba(226,232,240,0.8);font-size:20px;color:#000000;font-weight:300;}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">${reportLabel}</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userEmail}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">All Time</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Orders</div>
            <div class="report-info-value">${orders.length}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">Order ID</th>
            <th class="report-th">Items</th>
            <th class="report-th">Qty</th>
            <th class="report-th">Payment</th>
            <th class="report-th">Country</th>
            <th class="report-th">Amount</th>
            <th class="report-th">Reward Deducted</th>
          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows || `<tr><td class="report-td" colspan="8" style="text-align:center;font-style:italic;">No orders in this period</td></tr>`}
          <tr>
            <td class="report-total-td" colspan="6" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(total).toLocaleString("en-IN")}</td>
            <td class="report-total-td" style="color:#dc2626;font-weight:700;">₹${roundAmount(totalReward).toLocaleString("en-IN")}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

function generateSchedulePdfReport(transactions, userEmail, reportLabel, showReward = true) {
  if (!transactions || transactions.length === 0) return;

  const totalAmount = transactions.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
  const rewardCol = (v) => showReward ? `    <td class="report-td" style="color:${v > 0 ? "#dc2626" : "#000000"}">${v > 0 ? "₹" + Math.abs(v).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
  const rewardHeader = showReward ? `            <th class="report-th">Reward Deducted</th>\n` : "";
  const span = showReward ? 5 : 4;
  const labelSpan = 3;
  const totalReward = showReward ? `            <td class="report-total-td" style="color:#dc2626;font-weight:700;">₹${roundAmount(totalAmount).toLocaleString("en-IN")}</td>\n` : "";

  const rows = transactions
    .map((t) => {
      const payMethod = t.paymentMethod || "Reward";
      const payLabel = (t.rewardUsed || 0) > 0 && payMethod !== "Reward" && !payMethod.startsWith("Reward") ? "Reward + " + payMethod : payMethod;
      return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${t.label || "Schedule Meeting"}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">${(t.totalPrice || t.amount) > 0 ? "₹" + Math.abs(t.totalPrice || t.amount || 0).toLocaleString("en-IN") : "\u2014"}</td>
${rewardCol(t.rewardUsed || 0)}    </tr>`;
    })
    .join("");
  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${reportLabel} — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">${reportLabel}</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userEmail}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">All Time</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Payments</div>
            <div class="report-info-value">${transactions.length}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">Service</th>
            <th class="report-th">Payment</th>
            <th class="report-th">Price</th>
${rewardHeader}          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows || `<tr><td class="report-td" colspan="${span}" style="text-align:center;font-style:italic;">No payments in this period</td></tr>`}
          <tr>
            <td class="report-total-td" colspan="${labelSpan}" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(totalAmount).toLocaleString("en-IN")}</td>
${totalReward}          </tr>
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

function generateInterviewPdfReport(transactions, userEmail, showReward = true) {
  if (!transactions || transactions.length === 0) return;

  const rewardHeader = showReward ? `            <th class="report-th">Reward Deducted</th>\n` : "";
  const span = showReward ? 5 : 4;
  const labelSpan = 3;
  const totalDeducted = transactions.reduce((sum, t) => sum + (t.rewardUsed || t.amount || 0), 0);
  const totalReward = showReward ? `            <td class="report-total-td" style="color:#dc2626;font-weight:700;">₹${roundAmount(totalDeducted).toLocaleString("en-IN")}</td>\n` : "";

  const rows = transactions
    .map((t) => {
      const payMethod = t.paymentMethod || "Reward";
      const rewardAmt = t.rewardUsed || t.amount || 0;
      const payLabel = rewardAmt > 0 && payMethod !== "Reward" ? "Reward + " + payMethod : payMethod;
      const rewardCell = showReward ? `      <td class="report-td" style="color:${rewardAmt > 0 ? "#dc2626" : "#000000"}">${rewardAmt > 0 ? "₹" + roundAmount(rewardAmt).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
      return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${t.label || "AI Interview Payment"}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">₹${roundAmount(t.totalPrice || 0).toLocaleString("en-IN")}</td>
${rewardCell}    </tr>`;
    })
    .join("");

  const totalPrice = transactions.reduce((sum, t) => sum + (t.totalPrice || 0), 0);
  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>AI Interview Payments — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">AI Interview Payments</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userEmail}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">All Time</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Payments</div>
            <div class="report-info-value">${transactions.length}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">Module</th>
            <th class="report-th">Payment</th>
            <th class="report-th">Price</th>
${rewardHeader}          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows || `<tr><td class="report-td" colspan="${span}" style="text-align:center;font-style:italic;">No payments in this period</td></tr>`}
          <tr>
            <td class="report-total-td" colspan="${labelSpan}" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(totalPrice).toLocaleString("en-IN")}</td>
${totalReward}          </tr>
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

function generatePassportPdfReport(transactions, userEmail, reportLabel, showReward = true) {
  if (!transactions || transactions.length === 0) return;

  const totalAmount = transactions.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
  const rewardCol = (v) => showReward ? `    <td class="report-td" style="color:${v > 0 ? "#dc2626" : "#000000"}">${v > 0 ? "₹" + Math.abs(v).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
  const rewardHeader = showReward ? `            <th class="report-th">Reward Deducted</th>\n` : "";
  const span = showReward ? 5 : 4;
  const labelSpan = 3;
  const totalReward = showReward ? `            <td class="report-total-td" style="color:#dc2626;font-weight:700;">₹${roundAmount(totalAmount).toLocaleString("en-IN")}</td>\n` : "";

  const rows = transactions
    .map((t) => {
      const payMethod = t.paymentMethod || "Reward";
      const payLabel = (t.rewardUsed || 0) > 0 && payMethod !== "Reward" && !payMethod.startsWith("Reward") ? "Reward + " + payMethod : payMethod;
      return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${t.label || "Passport"}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">${(t.totalPrice || t.amount) > 0 ? "₹" + Math.abs(t.totalPrice || t.amount || 0).toLocaleString("en-IN") : "\u2014"}</td>
${rewardCol(t.rewardUsed || 0)}    </tr>`;
    })
    .join("");
  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${reportLabel} — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">${reportLabel}</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userEmail}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">All Time</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Payments</div>
            <div class="report-info-value">${transactions.length}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">Module</th>
            <th class="report-th">Payment</th>
            <th class="report-th">Price</th>
${rewardHeader}          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows || `<tr><td class="report-td" colspan="${span}" style="text-align:center;font-style:italic;">No payments in this period</td></tr>`}
          <tr>
            <td class="report-total-td" colspan="${labelSpan}" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(totalAmount).toLocaleString("en-IN")}</td>
${totalReward}          </tr>
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

function generateEsimPdfReport(orders, userEmail, showReward = true) {
  if (!orders || orders.length === 0) return;

  const rewardHeader = showReward ? `            <th class="report-th">Reward Deducted</th>\n` : "";
  const span = showReward ? 5 : 4;
  const labelSpan = 3;
  const totalDeducted = orders.reduce((sum, o) => sum + (o.rewardUsed || 0), 0);
  const totalReward = showReward ? `            <td class="report-total-td" style="color:#dc2626;font-weight:700;">₹${roundAmount(totalDeducted).toLocaleString("en-IN")}</td>\n` : "";

  const rows = orders
    .map((o) => {
      const label = `eSIM${o.country ? ` — ${o.country}` : ""}${o.productName ? ` ${o.productName.replace("eSIM - ", "")}` : ""}`;
      const payMethod = o.paymentMethod || "Reward";
      const payLabel = (o.rewardUsed || 0) > 0 && payMethod !== "Reward" && !payMethod.startsWith("Reward") ? "Reward + " + payMethod : (o.rewardUsed || 0) > 0 ? "Reward" : payMethod;
      const rewardCell = showReward ? `      <td class="report-td" style="color:${(o.rewardUsed || 0) > 0 ? "#dc2626" : "#000000"}">${(o.rewardUsed || 0) > 0 ? "₹" + roundAmount(o.rewardUsed || 0).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
      return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${o.date ? new Date(o.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${label}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">₹${roundAmount(o.totalPrice || o.total || o.amount || 0).toLocaleString("en-IN")}</td>
${rewardCell}    </tr>`;
    })
    .join("");

  const totalPrice = orders.reduce((sum, o) => sum + (o.totalPrice || o.total || o.amount || 0), 0);
  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>eSIM Purchases — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">eSIM Purchases</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userEmail}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">All Time</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Purchases</div>
            <div class="report-info-value">${orders.length}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">eSIM</th>
            <th class="report-th">Payment</th>
            <th class="report-th">Price</th>
${rewardHeader}          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows || `<tr><td class="report-td" colspan="${span}" style="text-align:center;font-style:italic;">No purchases in this period</td></tr>`}
          <tr>
            <td class="report-total-td" colspan="${labelSpan}" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(totalPrice).toLocaleString("en-IN")}</td>
${totalReward}          </tr>
        </tbody>
      </table>
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

function generateAllPdfReport(interviewTxns, scheduleTxns, passportTxns, esimOrders, userEmail, showReward = true) {
  const allPresent = interviewTxns.length > 0 || scheduleTxns.length > 0 || passportTxns.length > 0 || esimOrders.length > 0;
  if (!allPresent) return;

  const rewardHeader = showReward ? `            <th class="report-th">Reward Deducted</th>\n` : "";
  const span = showReward ? 5 : 4;
  const labelSpan = 3;

  const logoHtml = `<img src="${receiptLogo}" alt="TheVisaCo" style="width:100%;max-height:120px;object-fit:contain;object-position:left top;" />`;

  function buildTable(title, rows, totalAmount, totalRewardAmt) {
    if (!rows) return "";
    const rewardCol = (v) => showReward ? `      <td class="report-td" style="color:${v > 0 ? "#dc2626" : "#000000"}">${v > 0 ? "₹" + Math.abs(v).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
    const totalReward = showReward ? `            <td class="report-total-td" style="color:#dc2626;font-weight:700;">₹${roundAmount(totalRewardAmt).toLocaleString("en-IN")}</td>\n` : "";
    return `
      <div class="report-section-title" style="font-size:16px;font-weight:700;margin:20px 0 10px;padding-bottom:6px;border-bottom:1px solid #e2e8f0;">${title}</div>
      <table class="report-table">
        <thead class="report-thead">
          <tr>
            <th class="report-th">Date</th>
            <th class="report-th">Service</th>
            <th class="report-th">Payment</th>
            <th class="report-th">Price</th>
${rewardHeader}          </tr>
        </thead>
        <tbody class="report-tbody">
          ${rows}
          <tr>
            <td class="report-total-td" colspan="${labelSpan}" style="text-align:right;text-transform:uppercase;letter-spacing:0.5px;">Total</td>
            <td class="report-total-td" style="font-weight:700;">₹${roundAmount(totalAmount).toLocaleString("en-IN")}</td>
${totalReward}          </tr>
        </tbody>
      </table>`;
  }

  const interviewRows = interviewTxns.map((t) => {
    const payMethod = t.paymentMethod || "Reward";
    const rewardAmt = t.rewardUsed || t.amount || 0;
    const payLabel = rewardAmt > 0 && payMethod !== "Reward" ? "Reward + " + payMethod : payMethod;
    const rewardCol = showReward ? `      <td class="report-td" style="color:${rewardAmt > 0 ? "#dc2626" : "#000000"}">${rewardAmt > 0 ? "₹" + roundAmount(rewardAmt).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
    return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${t.label || "AI Interview Payment"}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">₹${roundAmount(t.totalPrice || 0).toLocaleString("en-IN")}</td>
${rewardCol}    </tr>`;
  }).join("");

  const scheduleRows = scheduleTxns.map((t) => {
    const payMethod = t.paymentMethod || "Reward";
    const payLabel = (t.rewardUsed || 0) > 0 && payMethod !== "Reward" && !payMethod.startsWith("Reward") ? "Reward + " + payMethod : payMethod;
    const rewardCol = showReward ? `      <td class="report-td" style="color:${(t.rewardUsed || 0) > 0 ? "#dc2626" : "#000000"}">${(t.rewardUsed || 0) > 0 ? "₹" + Math.abs(t.rewardUsed || 0).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
    return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${t.label || "Schedule Meeting"}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">${(t.totalPrice || t.amount) > 0 ? "₹" + Math.abs(t.totalPrice || t.amount || 0).toLocaleString("en-IN") : "\u2014"}</td>
${rewardCol}    </tr>`;
  }).join("");

  const esimRows = esimOrders.map((o) => {
    const payMethod = o.paymentMethod || "Reward";
    const payLabel = (o.rewardUsed || 0) > 0 && payMethod !== "Reward" && !payMethod.startsWith("Reward") ? "Reward + " + payMethod : payMethod;
    const rewardCol = showReward ? `      <td class="report-td" style="color:${(o.rewardUsed || 0) > 0 ? "#dc2626" : "#000000"}">${(o.rewardUsed || 0) > 0 ? "₹" + roundAmount(o.rewardUsed || 0).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
    return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${o.date ? new Date(o.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">eSIM Purchase${o.country ? " — " + o.country : ""}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">₹${roundAmount(o.totalPrice || o.total || o.amount || 0).toLocaleString("en-IN")}</td>
${rewardCol}    </tr>`;
  }).join("");

  const passportRows = passportTxns.map((t) => {
    const payMethod = t.paymentMethod || "Reward";
    const payLabel = (t.rewardUsed || 0) > 0 && payMethod !== "Reward" && !payMethod.startsWith("Reward") ? "Reward + " + payMethod : payMethod;
    const rewardCol = showReward ? `      <td class="report-td" style="color:${(t.rewardUsed || 0) > 0 ? "#dc2626" : "#000000"}">${(t.rewardUsed || 0) > 0 ? "₹" + Math.abs(t.rewardUsed || 0).toLocaleString("en-IN") : "\u2014"}</td>\n` : "";
    return `
    <tr>
      <td class="report-td" style="white-space:nowrap">${t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</td>
      <td class="report-td">${t.label || "Passport Payment"}</td>
      <td class="report-td">${payLabel}</td>
      <td class="report-td">${(t.totalPrice || t.amount) > 0 ? "₹" + Math.abs(t.totalPrice || t.amount || 0).toLocaleString("en-IN") : "\u2014"}</td>
${rewardCol}    </tr>`;
  }).join("");

  const totalAmount = interviewTxns.reduce((s, t) => s + (t.totalPrice || 0), 0)
    + scheduleTxns.reduce((s, t) => s + Math.abs(t.totalPrice || t.amount || 0), 0)
    + passportTxns.reduce((s, t) => s + Math.abs(t.totalPrice || t.amount || 0), 0)
    + esimOrders.reduce((s, o) => s + (o.totalPrice || o.total || o.amount || 0), 0);

  const totalCount = interviewTxns.length + scheduleTxns.length + passportTxns.length + esimOrders.length;

  const printContent = `<!DOCTYPE html>
<html lang="en">
<head>
<base href="${window.location.origin}/">
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>All Payments Report — TheVisaCo</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;font-family:'Inter','Segoe UI',sans-serif;}
  body{background:#eef2f7;padding:0 20px 20px;color:#000000;}
  .report-container{max-width:1040px;margin:auto;}
  .report-card{background:rgba(255,255,255,0.72);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-radius:32px;padding:4px 32px 28px;box-shadow:0 10px 40px rgba(15,23,42,0.06),inset 0 1px 0 rgba(255,255,255,0.65);}
  .report-top-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:24px;}
  .report-left-side{display:flex;flex-direction:column;gap:0;flex-shrink:0;}
  .report-logo-box{width:300px;min-height:124px;display:flex;align-items:center;justify-content:flex-start;overflow:hidden;}
  .report-logo-box img{width:100%;max-height:124px;object-fit:contain;object-position:left top;}
  .report-right-side{display:flex;flex-direction:column;align-items:flex-end;gap:12px;flex:0 0 auto;}
  .report-label{padding:10px 18px;border-radius:14px;background:rgba(255,255,255,0.6);font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;color:#000000;text-align:center;}
  .report-info-card{width:min(100%,420px);background:rgba(255,255,255,0.96);border-radius:24px;padding:16px 20px;border:1px solid rgba(148,163,184,0.45);box-shadow:0 12px 28px rgba(15,23,42,0.08);}
  .report-info-card-right{margin-top:0;}
  .report-info-grid{display:grid;grid-template-columns:auto minmax(0,1fr);row-gap:8px;column-gap:12px;}
  .report-info-grid > *:nth-last-child(n + 3){padding-bottom:8px;border-bottom:1px solid rgba(226,232,240,0.85);}
  .report-info-title{font-size:13px;color:#000000;font-weight:400;text-align:left;}
  .report-info-value{font-size:14px;color:#000000;font-weight:500;text-align:left;word-break:break-word;overflow-wrap:break-word;}
  .report-table-wrapper{margin-top:16px;overflow:hidden;border-radius:28px;background:rgba(255,255,255,0.5);box-shadow:0 5px 25px rgba(0,0,0,0.04);}
  .report-table{width:100%;border-collapse:collapse;margin-bottom:12px;}
  .report-thead{background:rgba(248,250,252,0.95);}
  .report-th{padding:12px 16px;text-align:center;font-size:12px;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#000000;border-left:1px solid rgba(226,232,240,0.75);border-right:1px solid rgba(226,232,240,0.75);}
  .report-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:14px;font-weight:300;color:#000000;text-align:center;}
  .report-tbody tr:hover{background:rgba(248,250,252,0.55);}
  .report-total-td{padding:14px 16px;border:1px solid rgba(226,232,240,0.75);font-size:15px;font-weight:700;color:#000000;text-align:center;background:rgba(248,250,252,0.95);}
  .report-footer{margin-top:20px;border-top:1px solid rgba(226,232,240,0.7);padding-top:12px;display:flex;justify-content:space-between;font-size:13px;color:#000000;font-weight:300;}
  .report-section-title{font-size:16px;font-weight:700;margin:20px 0 10px;padding-bottom:6px;border-bottom:1px solid #e2e8f0;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff;padding:0;}.report-container{max-width:none;}.report-card{padding:4px 16px 16px;box-shadow:none;border-radius:0;}.report-table-wrapper,.report-total-card{box-shadow:none;}.report-top-header{margin-bottom:10px;gap:18px;}.report-logo-box{width:250px;min-height:100px;}.report-logo-box img{max-height:100px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-table-wrapper{margin-top:12px;border-radius:0;}.report-th{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-total-td{padding:10px 12px;border:1px solid rgba(226,232,240,0.75);}.report-footer{margin-top:16px;padding-top:10px;}.report-info-card{width:360px;padding:12px 16px;box-shadow:none;}.report-info-value{word-break:break-word;overflow-wrap:break-word;}.report-info-grid{row-gap:6px;column-gap:8px;}}
  @media(max-width:768px){body{padding:10px 14px 16px;}.report-card{padding:10px 16px 16px;}.report-top-header{flex-direction:row;gap:16px;}.report-right-side{align-items:flex-end;flex:0 0 auto;}.report-info-card{width:280px;}.report-total-card{width:100%;}}
</style>
</head>
<body>
<div class="report-container">
  <div class="report-card">
    <div class="report-top-header">
      <div class="report-left-side">
        <div class="report-logo-box">${logoHtml}</div>
      </div>
      <div class="report-right-side">
        <div class="report-label">All Payments Report</div>
        <div class="report-info-card report-info-card-right">
          <div class="report-info-grid">
            <div class="report-info-title">User</div>
            <div class="report-info-value">${userEmail}</div>
            <div class="report-info-title">Period</div>
            <div class="report-info-value">All Time</div>
            <div class="report-info-title">Date Generated</div>
            <div class="report-info-value">${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div>
            <div class="report-info-title">Payments</div>
            <div class="report-info-value">${totalCount}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="report-table-wrapper">
      ${interviewTxns.length > 0 ? buildTable("AI Interview", interviewRows, interviewTxns.reduce((s, t) => s + (t.totalPrice || 0), 0), interviewTxns.reduce((s, t) => s + (t.rewardUsed || t.amount || 0), 0)) : ""}
      ${scheduleTxns.length > 0 ? buildTable("Schedule Meeting", scheduleRows, scheduleTxns.reduce((s, t) => s + Math.abs(t.totalPrice || t.amount || 0), 0), scheduleTxns.reduce((s, t) => s + (t.rewardUsed || 0), 0)) : ""}
      ${passportTxns.length > 0 ? buildTable("Passport", passportRows, passportTxns.reduce((s, t) => s + Math.abs(t.totalPrice || t.amount || 0), 0), passportTxns.reduce((s, t) => s + (t.rewardUsed || 0), 0)) : ""}
      ${esimOrders.length > 0 ? buildTable("eSIMs", esimRows, esimOrders.reduce((s, o) => s + (o.totalPrice || o.total || o.amount || 0), 0), esimOrders.reduce((s, o) => s + (o.rewardUsed || 0), 0)) : ""}
    </div>

    <div class="report-footer">
      <span>TheVisaCo &mdash; Confidential Report</span>
      <span>&copy; ${new Date().getFullYear()} TheVisaCo &mdash; support@thevisaco.com</span>
    </div>
  </div>
</div>
<script>
window.onafterprint = function() { window.close(); };
window.onload = function() {
  window.print();
  setTimeout(function() { window.close(); }, 1200);
};
window.onfocus = function() {
  setTimeout(function() { window.close(); }, 300);
};
<\/script>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}

// ────────────────────────────────────────────
//  REFERRAL CODE CARD
// ────────────────────────────────────────────
function ReferralCodeCard({ code, role }) {
  const [copied, setCopied] = useState(false);

  function copyCode() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Individual users ALWAYS see locked card — code only unlocks at member+
  if (!code || role === "individual") {
    return (
      <div className="ProfileReferralLockedCard">
        <div className="ProfileRlcIcon"><Icons.Lock /></div>
        <div>
          <div className="ProfileRlcTitle">Referral Code Locked</div>
          <div className="ProfileRlcSub">
            {role === "individual"
              ? "Upgrade to Member to unlock your referral code and start earning commissions. Individual accounts do not have access to referral codes."
              : "Complete your first paid application to unlock your referral code and start earning commissions."}
          </div>
        </div>
      </div>
    );
  }

  // Show what discount the code currently gives (based on referrer's current role)
  const discountPct = `${((REFERRAL_DISCOUNT_RATES[role] || REFERRAL_DISCOUNT_RATES.individual) * 100).toFixed(0)}%`;

  return (
    <div className="ProfileReferralCodeCard">
      <div className="ProfileRccEyebrow">Your Referral Code</div>
      <div className="ProfileRccCode">{code}</div>
      <div className="ProfileRccSub">
        Share this code — whoever uses it gets a <strong>{discountPct} discount</strong> on their service fee,
        and you earn commission when they pay.
      </div>
      <button
        className="ProfileBtn ProfileBtnPrimary ProfileBtnSm ProfileRccCopy"
        onClick={copyCode}
      >
        {copied ? <><Icons.Check /> Copied!</> : <><Icons.Copy /> Copy Code</>}
      </button>
    </div>
  );
}

// ────────────────────────────────────────────
//  MILESTONE SECTION (progress cards)
// ────────────────────────────────────────────
function MilestoneSection({ milestoneData, role }) {
  const totalSpent = milestoneData?.totalSvcFeeSpent || 0;
  const achieved   = milestoneData?.achievedMilestones || [];
  const rewardRate = MILESTONE_REWARD_RATES[role] || 0;
  const rewardPct  = `${(rewardRate * 100).toFixed(0)}%`;

  const startedAt = milestoneData?.startedAt;

  return (
    <div className="ProfileMilestoneSection" data-aos="fade-up" data-aos-delay="50">
      <div className="ProfileMilestoneSectionHeader" data-aos="fade-up" data-aos-delay="100">
        <div className="ProfileMilestoneSectionIcon"><Icons.Trophy /></div>
        <div>
          <div className="ProfileMilestoneSectionTitle">Milestone Rewards</div>
          <div className="ProfileMilestoneSectionSub">
            You earn <strong>{rewardPct}</strong> of your own service fee payment — accumulated toward your milestones.
          </div>
        </div>
      </div>

      <div className="ProfileMilestoneTotalSpend">
        <div className="ProfileMtsTotRow">
          <span className="ProfileMtsTotLabel">Total Service Fee Spent</span>
          <span className="ProfileMtsTotVal">₹{totalSpent.toLocaleString("en-IN")}</span>
        </div>
      </div>

      <div className="ProfileMilestoneCards">
        {MILESTONES.map((milestone, idx) => {
          const isAchieved = achieved.includes(milestone.id);
          const isCurrent  = !isAchieved && (idx === 0 || achieved.includes(MILESTONES[idx - 1]?.id));
          const isLocked   = !isAchieved && !isCurrent;

          const prevTarget   = idx > 0 ? MILESTONES[idx - 1].spendTarget : 0;
          const range        = milestone.spendTarget - prevTarget;
          const spentInRange = Math.min(Math.max(totalSpent - prevTarget, 0), range);
          const progressPct  = Math.min((spentInRange / range) * 100, 100);
          const remaining    = Math.max(milestone.spendTarget - totalSpent, 0);

          return (
            <div
              key={milestone.id}
              className={`ProfileMilestoneCard ${isAchieved ? "achieved" : ""} ${isCurrent ? "current" : ""} ${isLocked ? "locked" : ""}`}
            >
              <div className="ProfileMcHeader">
                <div className="ProfileMcIcon">{milestone.icon}</div>
                <div className="ProfileMcInfo">
                  <div className="ProfileMcTitle">{milestone.label}</div>
                  <div className="ProfileMcTarget">
                    Spend ₹{milestone.spendTarget.toLocaleString("en-IN")} in service fees
                  </div>
                </div>
                <div className="ProfileMcReward">
                  <div className="ProfileMcRewardPoints">+{milestone.pointReward.toLocaleString("en-IN")}</div>
                  <div className="ProfileMcRewardLabel">bonus pts</div>
                </div>
              </div>

              {isAchieved ? (
                <div className="ProfileMcAchievedBadge">
                  <Icons.Check /> Milestone Achieved! Bonus credited to your wallet.
                </div>
              ) : (
                <>
                  <div className="ProfileMcProgressTrack">
                    <div className="ProfileMcProgressFill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="ProfileMcProgressMeta">
                    <span className="ProfileMcProgressSpent">
                      ₹{Math.min(totalSpent, milestone.spendTarget).toLocaleString("en-IN")} /{" "}
                      ₹{milestone.spendTarget.toLocaleString("en-IN")}
                    </span>
                    {remaining > 0 && (
                      <span className="ProfileMcProgressRemaining">
                        <Icons.Zap /> ₹{remaining.toLocaleString("en-IN")} to go
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="ProfileMilestoneOngoing">
        <div className="ProfileMilestoneOngoingIcon"><Icons.TrendingUp /></div>
        <div>
          <div className="ProfileMilestoneOngoingTitle">Ongoing Reward: {rewardPct} per Payment</div>
          <div className="ProfileMilestoneOngoingSub">
            Every time you pay a service fee, <strong>{rewardPct}</strong> is instantly
            credited to your Redeemable balance — in addition to the milestone bonuses above.
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  PDF REPORT GENERATOR — glass-morphism design
//  Uses the Report component layout with ReceiptLogo
// ────────────────────────────────────────────
async function generatePdfReport(transactions, fromDate, toDate, userName, reportLabel, availableBalance) {
  const toBase64 = async (url) => {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return new Promise((r) => {
      const reader = new FileReader();
      reader.onloadend = () => r(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  let logoSrc = null;
  try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

  const html = generateEarningReportHTML({
    transactions,
    userName,
    fromDate,
    toDate,
    reportLabel,
    logoSrc,
    availableBalance,
  });

  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}

// ────────────────────────────────────────────
//  SHARED TRANSACTION HISTORY SECTION
// ────────────────────────────────────────────
function TransactionHistorySection({
  transactions,
  sectionTitle,
  totalLabel,
  userName,
  reportLabel,
  availableBalance,
  onRequestPayout,
  showPayoutBar,
}) {
  const [showAll,     setShowAll]     = useState(false);
  const [search,      setSearch]      = useState("");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [amountMin,   setAmountMin]   = useState("");
  const [amountMax,   setAmountMax]   = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [groupBy,     setGroupBy]     = useState("none");

  const hasActiveFilters = search || dateFrom || dateTo || amountMin || amountMax;

  function clearFilters() {
    setSearch(""); setDateFrom(""); setDateTo(""); setAmountMin(""); setAmountMax("");
  }

  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      const d = new Date(t.date);
      if (search && !t.label.toLowerCase().includes(search.toLowerCase()) && !t.note?.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
      if (amountMin && Math.abs(t.amount) < parseFloat(amountMin)) return false;
      if (amountMax && Math.abs(t.amount) > parseFloat(amountMax)) return false;
      return true;
    });
  }, [transactions, search, dateFrom, dateTo, amountMin, amountMax]);

  const filteredWithBalance = useMemo(() => {
    const oldestFirst = [...filtered].reverse();
    let running = 0;
    const withBal = oldestFirst.map((t) => {
      const prev = running;
      running += t.type === "credit" ? t.amount : -Math.abs(t.amount);
      return { ...t, _prevBalance: prev, _newBalance: running };
    });
    return withBal.reverse();
  }, [filtered]);

  const groupedItems = useMemo(() => {
    if (groupBy === "none") return filteredWithBalance;
    const items = [];
    let lastKey = null;
    filteredWithBalance.forEach((t) => {
      const d   = new Date(t.date);
      const key = groupBy === "month"
        ? `${d.toLocaleString("en-IN", { month: "long" })} ${d.getFullYear()}`
        : `${d.getFullYear()}`;
      if (key !== lastKey) { items.push({ __divider: true, key }); lastKey = key; }
      items.push(t);
    });
    return items;
  }, [filteredWithBalance, groupBy]);

  const displayed   = showAll ? groupedItems : groupedItems.slice(0, groupBy === "none" ? 5 : 12);
  const totalEarned = transactions.filter((t) => t.type === "credit").reduce((s, t) => s + t.amount, 0);

  return (
    <div className="ProfileCommHistorySection" data-aos="fade-up" data-aos-delay="100">
      <div className="ProfileCommHistoryHeader" data-aos="fade-up" data-aos-delay="50">
        <div>
          <div className="ProfileTxnSectionTitle">{sectionTitle}</div>
          <div className="ProfileCommTotalBadge">
            {totalLabel}: ₹{totalEarned.toLocaleString("en-IN")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div className="ProfileGroupByRow">
            <button className={`ProfileGroupByBtn ${groupBy === "none" ? "active" : ""}`} onClick={() => setGroupBy("none")}>All</button>
            <button className={`ProfileGroupByBtn ${groupBy === "month" ? "active" : ""}`} onClick={() => setGroupBy("month")}><Icons.Calendar /> Month</button>
            <button className={`ProfileGroupByBtn ${groupBy === "year" ? "active" : ""}`} onClick={() => setGroupBy("year")}><Icons.Calendar /> Year</button>
          </div>
          <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" onClick={() => setShowFilters(!showFilters)}>
            <Icons.Filter /> Filters {hasActiveFilters && <span className="ProfileFilterActiveDot" />}
          </button>
          <button
            className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
            onClick={() => generatePdfReport(transactions, dateFrom, dateTo, userName, reportLabel, availableBalance)}
            title="Download PDF Report"
          >
            <Icons.Download /> PDF
          </button>
        </div>
      </div>

      <div className="ProfileCommSearchRow">
        <div className="ProfileCommSearchInput">
          <Icons.Search />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search transactions…" />
          {search && (
            <button className="ProfileSearchClearBtn" onClick={() => setSearch("")} title="Clear search">
              <Icons.Close />
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="ProfileCommFiltersPanel">
          <div className="ProfilePfField">
            <label><Icons.Calendar /> From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="ProfilePfField">
            <label><Icons.Calendar /> To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="ProfilePfField">
            <label>Min Amount (₹)</label>
            <input type="number" min="0" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} placeholder="0" />
          </div>
          <div className="ProfilePfField">
            <label>Max Amount (₹)</label>
            <input type="number" min="0" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} placeholder="Any" />
          </div>
          {hasActiveFilters && (
            <div className="ProfilePfField ProfilePfField--full" style={{ alignSelf: "flex-end" }}>
              <button
                className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                style={{ color: "var(--Profile-red)", borderColor: "var(--Profile-red-border)", width: "100%" }}
                onClick={clearFilters}
              >
                <Icons.XCircle /> Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="ProfileTxnEmpty">
          No transactions match your filters.
          {hasActiveFilters && (
            <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" style={{ marginTop: 10 }} onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="ProfileTxnList">
          {displayed.map((item, idx) => {
            if (item.__divider) {
              return (
                <div key={`divider_${item.key}_${idx}`} className="ProfileTxnDivider">
                  <span className="ProfileTxnDividerLabel">{item.key}</span>
                </div>
              );
            }
            return (
              <div key={item.id} className="ProfileTxnRow">
                <div className={`ProfileTxnRowDot ${item.type === "credit" ? "credit" : "debit"}`} />
                <div className="ProfileTxnRowInfo">
                  <div className="ProfileTxnRowLabel">{item.label}</div>
                  <div className="ProfileTxnRowNote">{item.note}</div>
                  <div className="ProfileTxnRowDate">{formatDate(item.date)}</div>
                  <div className="ProfileTxnRowBalance">
                    <span className="ProfileTxnBalPrev">₹{item._prevBalance.toLocaleString("en-IN")}</span>
                    <span className="ProfileTxnBalArrow"></span>
                    <span className="ProfileTxnBalNew">₹{item._newBalance.toLocaleString("en-IN")}</span>
                  </div>
                </div>
                <div className={`ProfileTxnRowAmount ${item.type === "credit" ? "credit" : "debit"}`}>
                  {item.type === "credit" ? "+" : "−"}₹{Math.abs(item.amount).toLocaleString("en-IN")}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > (groupBy === "none" ? 5 : 12) && (
        <button
          className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
          style={{ width: "100%", marginTop: 10 }}
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Show Less" : `View All (${filtered.length})`}
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
//  COMMISSION + MILESTONE HISTORY PANEL
//  Single history section with sub-label tags
//  Request Payout always shown at bottom
// ────────────────────────────────────────────
function RedeemableHistoryPanel({
  reTxns,
  role,
  userName,
  availableBalance,
  onRequestPayout,
}) {
  /* Shopping history - commented out for future use
  const SHOPPING_LABELS = ["Shopping", "Shop", "Order", "Voucher Redemption", "Points Used", "Points Order"];
  const isShoppingTxn = (t) => SHOPPING_LABELS.some((lbl) => (t.label || "").toLowerCase().includes(lbl.toLowerCase()));
  const shoppingTxns = reTxns.filter((t) => isShoppingTxn(t));
  */
  // Hide commission entries from individual profiles — only milestone rewards shown
  const isInterviewTxn = (t) => (t.label || "").toLowerCase().includes("ai interview");
  const earningTxns = (role === "individual" ? reTxns.filter((t) => t.isMilestone === true) : reTxns).filter((t) => !isInterviewTxn(t));

  const commissionTxns = useMemo(
    () => earningTxns.filter((t) => t.isMilestone !== true),
    [earningTxns]
  );

  const milestoneTxns = useMemo(
    () => earningTxns.filter((t) => t.isMilestone === true),
    [earningTxns]
  );

  // Merge all transactions, each tagged with its type for display
  const allTxns = useMemo(() => {
    return earningTxns.map((t) => ({
      ...t,
      _sectionTag: t.isMilestone ? "Milestone" : "Commission",
    }));
  }, [earningTxns]);

  const commRate     = `${((COMMISSION_RATES[role] || 0) * 100).toFixed(0)}%`;
  const discountRate = `${((REFERRAL_DISCOUNT_RATES[role] || 0) * 100).toFixed(0)}%`;

  return (
    <div className="ProfileHistoryPanel" data-aos="fade-up" data-aos-delay="300">
      {/* Single combined history */}
      {allTxns.length === 0 ? (
        <div className="ProfileTxnEmpty">
          No earnings yet.{" "}
          {role !== "individual"
            ? `Share your referral code — when someone else pays using it, you earn ${commRate} commission. They get a ${discountRate} discount.`
            : "Pay service fees to start accumulating milestone rewards."}
        </div>
      ) : (
        <CombinedHistorySection
          transactions={allTxns}
          commissionTxns={commissionTxns}
          milestoneTxns={milestoneTxns}
          userName={userName}
          role={role}
        />
      )}

      {/* Request Payout — ALWAYS shown at bottom */}
      <div className="ProfileRequestPayoutBar">
        <div className="ProfileRpbHeader">
         
          <div>
            <div className="ProfileRpbTitle">Available balance: <strong>₹{(availableBalance || 0).toLocaleString("en-IN")}</strong> </div>
            
          </div>
        </div>
        <button
          className="ProfileBtnPay"
          style={{ padding: "8px 20px", fontSize: 12.5 }}
          onClick={onRequestPayout}
          disabled={!availableBalance || availableBalance <= 0}
        >
          <Icons.CreditCard /> Request Payout
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  COMBINED HISTORY SECTION
//  Shows all transactions with type tags
// ────────────────────────────────────────────
function CombinedHistorySection({ transactions, commissionTxns, milestoneTxns, userName, role }) {
  // Filter out eSIM purchases and AI Interview payments from earnings history
  const ESIM_LABELS = ["esim", "esim purchase", "sim purchase"];
  const isEsimTxn = (t) => ESIM_LABELS.some((lbl) => (t.label || "").toLowerCase().includes(lbl));
  const isInterviewTxn = (t) => (t.label || "").toLowerCase().includes("ai interview");
  const filteredTxns = transactions.filter((t) => !isEsimTxn(t) && !isInterviewTxn(t));
  
  const [showAll,     setShowAll]     = useState(false);
  const [search,      setSearch]      = useState("");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [typeFilter,  setTypeFilter]  = useState("all"); // "all" | "commission" | "milestone"
  const [showFilters, setShowFilters] = useState(false);
  const [groupBy,     setGroupBy]     = useState("none");

  const hasActiveFilters = search || dateFrom || dateTo || typeFilter !== "all";

  function clearFilters() {
    setSearch(""); setDateFrom(""); setDateTo(""); setTypeFilter("all");
  }

  // Filter out eSIM transactions before processing
  const filteredSourceList = useMemo(() => {
    if (typeFilter === "commission") return commissionTxns.map((t) => ({ ...t, _sectionTag: "Commission" })).filter((t) => !isEsimTxn(t));
    if (typeFilter === "milestone")  return milestoneTxns.map((t) => ({ ...t, _sectionTag: "Milestone" })).filter((t) => !isEsimTxn(t));
    return filteredTxns;
  }, [filteredTxns, commissionTxns, milestoneTxns, typeFilter]);

  const filtered = useMemo(() => {
    return filteredSourceList.filter((t) => {
      const d = new Date(t.date);
      if (search && !t.label.toLowerCase().includes(search.toLowerCase()) && !t.note?.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }, [filteredSourceList, search, dateFrom, dateTo]);

  const filteredWithBalance = useMemo(() => {
    const oldestFirst = [...filtered].reverse();
    let running = 0;
    const withBal = oldestFirst.map((t) => {
      const prev = running;
      running += t.type === "credit" ? t.amount : -Math.abs(t.amount);
      return { ...t, _prevBalance: prev, _newBalance: running };
    });
    return withBal.reverse();
  }, [filtered]);

  const groupedItems = useMemo(() => {
    if (groupBy === "none") return filteredWithBalance;
    const items = [];
    let lastKey = null;
    filteredWithBalance.forEach((t) => {
      const d   = new Date(t.date);
      const key = groupBy === "month"
        ? `${d.toLocaleString("en-IN", { month: "long" })} ${d.getFullYear()}`
        : `${d.getFullYear()}`;
      if (key !== lastKey) { items.push({ __divider: true, key }); lastKey = key; }
      items.push(t);
    });
    return items;
  }, [filteredWithBalance, groupBy]);

  const displayed   = showAll ? groupedItems : groupedItems.slice(0, groupBy === "none" ? 5 : 12);
  const totalEarned = filtered.filter((t) => t.type === "credit").reduce((s, t) => s + t.amount, 0);

  // Choose report label and data based on filter
  const reportLabel = typeFilter === "commission" ? "Commission History"
                    : typeFilter === "milestone"  ? "Milestone Earnings"
                    : "Redeemable Wallet History";

  return (
    <div className="ProfileCommHistorySection">
      <div className="ProfileCommHistoryHeader">
        <div>
          <div className="ProfileTxnSectionTitle">Earnings History</div>
          <div className="ProfileCommTotalBadge">
            Total Earned: ₹{totalEarned.toLocaleString("en-IN")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div className="ProfileGroupByRow">
            <button className={`ProfileGroupByBtn ${groupBy === "none" ? "active" : ""}`} onClick={() => setGroupBy("none")}>All</button>
            <button className={`ProfileGroupByBtn ${groupBy === "month" ? "active" : ""}`} onClick={() => setGroupBy("month")}><Icons.Calendar /> Month</button>
            <button className={`ProfileGroupByBtn ${groupBy === "year" ? "active" : ""}`} onClick={() => setGroupBy("year")}><Icons.Calendar /> Year</button>
          </div>
          <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" onClick={() => setShowFilters(!showFilters)}>
            <Icons.Filter /> Filters {hasActiveFilters && <span className="ProfileFilterActiveDot" />}
          </button>
          <button
            className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
            onClick={() => generatePdfReport(filtered, dateFrom, dateTo, userName, reportLabel)}
            title="Download PDF Report"
          >
            <Icons.Download /> PDF
          </button>
        </div>
      </div>

      {/* Type filter tabs */}
      <div className="ProfileHistoryTypeFilterRow">
        <button
          className={`ProfileHistoryTypeBtn ${typeFilter === "all" ? "active" : ""}`}
          onClick={() => setTypeFilter("all")}
        >
          All <span className="ProfileHistoryTabCount">{filteredTxns.length}</span>
        </button>
        {role !== "individual" && (
          <button
            className={`ProfileHistoryTypeBtn commission ${typeFilter === "commission" ? "active" : ""}`}
            onClick={() => setTypeFilter("commission")}
          >
            <Icons.TrendingUp /> Commission
            {commissionTxns.filter((t) => !isEsimTxn(t)).length > 0 && <span className="ProfileHistoryTabCount">{commissionTxns.filter((t) => !isEsimTxn(t)).length}</span>}
          </button>
        )}
        <button
          className={`ProfileHistoryTypeBtn milestone ${typeFilter === "milestone" ? "active" : ""}`}
          onClick={() => setTypeFilter("milestone")}
        >
          <Icons.Trophy /> Milestone
          {milestoneTxns.filter((t) => !isEsimTxn(t)).length > 0 && <span className="ProfileHistoryTabCount">{milestoneTxns.filter((t) => !isEsimTxn(t)).length}</span>}
        </button>
      </div>

      <div className="ProfileCommSearchRow">
        <div className="ProfileCommSearchInput">
          <Icons.Search />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search transactions…" />
          {search && (
            <button className="ProfileSearchClearBtn" onClick={() => setSearch("")} title="Clear search">
              <Icons.Close />
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="ProfileCommFiltersPanel">
          <div className="ProfilePfField">
            <label><Icons.Calendar /> From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="ProfilePfField">
            <label><Icons.Calendar /> To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          {hasActiveFilters && (
            <div className="ProfilePfField ProfilePfField--full" style={{ alignSelf: "flex-end" }}>
              <button
                className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                style={{ color: "var(--Profile-red)", borderColor: "var(--Profile-red-border)", width: "100%" }}
                onClick={clearFilters}
              >
                <Icons.XCircle /> Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="ProfileTxnEmpty">
          No transactions match your filters.
          {hasActiveFilters && (
            <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" style={{ marginTop: 10 }} onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="ProfileTxnList">
          {displayed.map((item, idx) => {
            if (item.__divider) {
              return (
                <div key={`divider_${item.key}_${idx}`} className="ProfileTxnDivider">
                  <span className="ProfileTxnDividerLabel">{item.key}</span>
                </div>
              );
            }
            const isReferralCommission = item._sectionTag === "Commission" && item.applicantName;
            const isMilestoneTxn = item._sectionTag === "Milestone" && item.isMilestone;
            const showTxnDetails = (isReferralCommission || isMilestoneTxn) && (item.country || item.percentage);
            return (
              <div key={item.id} className="ProfileTxnRow">
                <div className={`ProfileTxnRowDot ${item.type === "credit" ? "credit" : "debit"}`} />
                <div className="ProfileTxnRowInfo">
                  <div className="ProfileTxnRowLabelRow">
                    <span className="ProfileTxnRowLabel">{item.label}</span>
                    <span className={`ProfileTxnTypeTag ProfileTxnTypeTag--${item._sectionTag === "Milestone" ? "milestone" : "commission"}`}>
                      {item._sectionTag}
                    </span>
                  </div>
                  {item.applicantName && (
                    <div className="ProfileTxnRowNote" style={{ color: "var(--Profile-text-3)", marginBottom: 2 }}>
                      Applicant: <strong>{item.applicantName}</strong>
                    </div>
                  )}
                  {showTxnDetails ? (
                    <div className="ProfileTxnRowNote">
                      {item.country && <span className="ProfileTxnCountry">{item.country}</span>}
                      {item.country && item.percentage && <span style={{ margin: "0 6px" }}>{"|"}</span>}
                      <span className="ProfileTxnApplicant">{item.percentage}{item.svcFee && ` of ${item.svcFee}`}</span>
                      <span style={{ margin: "0 6px" }}>{"|"}</span>
                      <span className="ProfileTxnRowDate">{formatDate(item.date)}</span>
                    </div>
                  ) : (
                    <>
                      <div className="ProfileTxnRowNote">{item.note}</div>
                      <div className="ProfileTxnRowDate">{formatDate(item.date)}</div>
                    </>
                  )}
                  <div className="ProfileTxnRowBalance">
                    <span className="ProfileTxnBalPrev">₹{item._prevBalance.toLocaleString("en-IN")}</span>
                    <span className="ProfileTxnBalArrow"></span>
                    <span className="ProfileTxnBalNew">₹{item._newBalance.toLocaleString("en-IN")}</span>
                  </div>
                </div>
                <div className={`ProfileTxnRowAmount ${item.type === "credit" ? "credit" : "debit"}`}>
                  {item.type === "credit" ? "+" : "−"}₹{Math.abs(item.amount).toLocaleString("en-IN")}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > (groupBy === "none" ? 5 : 12) && (
        <button
          className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
          style={{ width: "100%", marginTop: 10 }}
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Show Less" : `View All (${filtered.length})`}
        </button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
//  VOUCHER CARD
// ────────────────────────────────────────────
function VoucherCard({ voucher, onRedeem, onUploadBill }) {
  const [copied, setCopied] = useState(false);

  function copyCode() {
    navigator.clipboard.writeText(voucher.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="ProfileVoucherCard" style={{ borderColor: `${voucher.color}30` }}>
      <div className="ProfileVoucherCardTop" style={{ background: voucher.bgColor }}>
        <div className="ProfileVoucherLogo" style={{ fontSize: 28 }}>{voucher.logo}</div>
        <div className="ProfileVoucherBrand" style={{ color: voucher.color }}>{voucher.brand}</div>
        <div className="ProfileVoucherDiscount">{voucher.discount}</div>
        <span className="ProfileVoucherCategory">{voucher.category}</span>
      </div>
      <div className="ProfileVoucherCardBody">
        <div className="ProfileVoucherDesc">{voucher.description}</div>
        <div className="ProfileVoucherCode" onClick={copyCode}>
          <span><Icons.Tag /> {voucher.code}</span>
          <span className="ProfileVoucherCopyBtn">{copied ? <Icons.Check /> : <Icons.Copy />}</span>
        </div>
        <div className="ProfileVoucherExpiry">Valid till {voucher.expiry}</div>
      </div>
      <div className="ProfileVoucherCardFooter">
        {voucher.allowBillUpload ? (
          <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" style={{ flex: 1 }} onClick={() => onUploadBill(voucher)}>
            <Icons.Upload /> Upload Bill &amp; Redeem
          </button>
        ) : (
          <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" style={{ flex: 1 }} onClick={() => onRedeem(voucher)}>
            <Icons.Gift /> Redeem Voucher
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  BILL UPLOAD MODAL
// ────────────────────────────────────────────
function BillUploadModal({ voucher, onClose, onSubmit }) {
  const fileInputRef                  = useRef();
  const [offerName, setOfferName]     = useState(voucher ? voucher.brand : "");
  const [amount, setAmount]           = useState("");
  const [bankDetails, setBankDetails] = useState({ accountNumber: "", ifsc: "", micr: "", customerId: "" });
  const [billFile, setBillFile]       = useState(null);
  const [chequeFile, setChequeFile]   = useState(null);
  const chequeInputRef                = useRef();
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);

  async function handleSubmit() {
    if (!offerName.trim()) return alert("Please enter the offer name.");
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) return alert("Please enter a valid amount.");
    if (!billFile) return alert("Please upload your bill.");
    if (!bankDetails.accountNumber.trim() || !bankDetails.ifsc.trim()) return alert("Please enter bank account details.");
    if (!chequeFile) return alert("Please upload a photo of your cancelled cheque.");
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    await onSubmit({ offerName, amount, paymentMode: "bank", mobileNumber: "", bankDetails, billFile, chequeFile });
    setSubmitting(false);
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="ProfilePayOverlay">
        <div className="ProfilePayModal" style={{ maxWidth: 420 }}>
          <div className="ProfileBillSuccessBody">
            <div className="ProfileBillSuccessIcon">✓</div>
            <h3>Request Submitted!</h3>
            <p>We will process your payment shortly and get back to you.</p>
            <div className="ProfileBillSuccessNote">📧 A confirmation has been sent to our team.</div>
            <button className="ProfileBtn ProfileBtnPrimary" style={{ marginTop: 20 }} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ProfilePayOverlay">
      <div className="ProfilePayModal ProfilePayModal--wide">
        <div className="ProfilePayModalHeader">
          <div className="ProfilePayModalTitle">
            <div className="ProfilePayModalFlag"><Icons.Receipt /></div>
            <div>
              <div className="ProfilePayModalCountry">Post Your Bill</div>
              <div className="ProfilePayModalType">{voucher ? `${voucher.brand} — ${voucher.discount}` : "Bill Upload"}</div>
            </div>
          </div>
          <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
        </div>
        <div className="ProfilePayModalBody" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="ProfilePfField">
            <label>Offer / Brand Name *</label>
            <input value={offerName} onChange={(e) => setOfferName(e.target.value)} placeholder="e.g. Domino's Pizza" />
          </div>
          <div className="ProfilePfField">
            <label>Amount Paid (₹) *</label>
            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 850" />
          </div>
          <div className="ProfilePfField">
            <label>Upload Bill *</label>
            <div className="ProfileBillFileArea" onClick={() => fileInputRef.current?.click()}>
              {billFile ? <><Icons.Check /> {billFile.name}</> : <><Icons.Upload /> Click to upload bill (PDF, JPG, PNG)</>}
            </div>
            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={(e) => setBillFile(e.target.files?.[0] || null)} />
          </div>

          {/* Bank Details */}
          <div className="ProfilePfField">
            <label><Icons.Bank /> Bank Transfer Details *</label>
          </div>
          <div className="ProfileBankDetailsGrid">
            <div className="ProfilePfField"><label>Account Number *</label><input value={bankDetails.accountNumber} onChange={(e) => setBankDetails((p) => ({ ...p, accountNumber: e.target.value }))} placeholder="1234567890" /></div>
            <div className="ProfilePfField"><label>IFSC Code *</label><input value={bankDetails.ifsc} onChange={(e) => setBankDetails((p) => ({ ...p, ifsc: e.target.value.toUpperCase() }))} placeholder="SBIN0001234" /></div>
            <div className="ProfilePfField"><label>MICR Code</label><input value={bankDetails.micr} onChange={(e) => setBankDetails((p) => ({ ...p, micr: e.target.value }))} placeholder="110002009" /></div>
            <div className="ProfilePfField"><label>Customer ID</label><input value={bankDetails.customerId} onChange={(e) => setBankDetails((p) => ({ ...p, customerId: e.target.value }))} placeholder="CIF / Customer ID" /></div>
          </div>

          {/* Cancelled Cheque Upload */}
          <div className="ProfilePfField">
            <label>Upload Cancelled Cheque Photo *</label>
            <div
              className="ProfileBillFileArea ProfileBillFileArea--cheque"
              onClick={() => chequeInputRef.current?.click()}
            >
              {chequeFile
                ? <><Icons.Check /> {chequeFile.name}</>
                : <><Icons.Upload /> Click to upload cancelled cheque (JPG, PNG, PDF)</>}
            </div>
            <input
              ref={chequeInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e) => setChequeFile(e.target.files?.[0] || null)}
            />
            <div style={{ fontSize: 11, color: "var(--Profile-text-3)", marginTop: 4 }}>
              Please upload a clear photo of a cancelled cheque bearing your account number and IFSC.
            </div>
          </div>

          <div className="ProfilePayNote">
            <Icons.Info /> Once submitted, our team will verify your bill and process the payment within 1-2 business days.
          </div>
        </div>
        <div className="ProfilePayModalFooter">
          <button className="ProfileBtn ProfileBtnOutline" onClick={onClose}>Cancel</button>
          <button className="ProfileBtn ProfileBtnPrimary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="ProfileSpinner" /> Submitting…</> : <><Icons.Upload /> Submit Bill</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  VOUCHER REDEEM CONFIRMATION MODAL
// ────────────────────────────────────────────
function VoucherRedeemModal({ voucher, onClose, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const [done, setDone]             = useState(false);

  async function handleConfirm() {
    setConfirming(true);
    await new Promise((r) => setTimeout(r, 1000));
    await onConfirm(voucher);
    setConfirming(false);
    setDone(true);
  }

  if (done) {
    return (
      <div className="ProfilePayOverlay">
        <div className="ProfilePayModal" style={{ maxWidth: 400 }}>
          <div className="ProfileBillSuccessBody">
            <div className="ProfileBillSuccessIcon">✓</div>
            <h3>Redemption Requested!</h3>
            <p>We will process your voucher shortly.</p>
            <button className="ProfileBtn ProfileBtnPrimary" style={{ marginTop: 20 }} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ProfilePayOverlay">
      <div className="ProfilePayModal" style={{ maxWidth: 400 }}>
        <div className="ProfilePayModalHeader">
          <div className="ProfilePayModalTitle">
            <div className="ProfilePayModalFlag" style={{ fontSize: 20 }}>{voucher.logo}</div>
            <div>
              <div className="ProfilePayModalCountry">{voucher.brand}</div>
              <div className="ProfilePayModalType">Redeem {voucher.discount}</div>
            </div>
          </div>
          <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
        </div>
        <div className="ProfilePayModalBody">
          <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "var(--Profile-text-2)", marginBottom: 16 }}>
            You are requesting to redeem the <strong>{voucher.brand}</strong> voucher for <strong>{voucher.discount}</strong>.
          </p>
          <div className="ProfileVoucherRedeemDetail">
            <div><Icons.Tag /> Code: <strong>{voucher.code}</strong></div>
            <div><Icons.Calendar /> Expires: {voucher.expiry}</div>
          </div>
          <div className="ProfilePayNote" style={{ marginTop: 16 }}>
            <Icons.Info /> A notification will be sent to our admin team for processing.
          </div>
        </div>
        <div className="ProfilePayModalFooter">
          <button className="ProfileBtn ProfileBtnOutline" onClick={onClose}>Cancel</button>
          <button className="ProfileBtn ProfileBtnPrimary" onClick={handleConfirm} disabled={confirming}>
            {confirming ? <><span className="ProfileSpinner" /> Processing…</> : <><Icons.Gift /> Confirm Redeem</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  PAYOUT REQUEST MODAL
//  Bank transfer only — no mobile/UPI
//  Requires cancelled cheque photo
// ────────────────────────────────────────────
function PayoutRequestModal({ availableBalance, onClose, onSubmit, initialData, userEmail, onDelete }) {
  const [voidChequeData, setVoidChequeData] = useState(null);
  
  useEffect(() => {
    if (userEmail) {
      getDocFileData(userEmail, "void_cheque").then((data) => setVoidChequeData(data));
    }
  }, [userEmail]);
  
  const [amount, setAmount]           = useState(initialData?.amount ? String(initialData.amount) : (availableBalance > 0 ? String(availableBalance) : ""));
  const [bankDetails, setBankDetails] = useState(initialData?.bankDetails || { accountNumber: "", ifsc: "", micr: "", customerId: "" });
  const [chequeFile, setChequeFile]   = useState(initialData?.chequeFile || null);
  const chequeInputRef                = useRef();
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  
  const handleDownloadCheque = (dataUrl) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "void_cheque";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  async function handleSubmit() {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return alert("Please enter a valid amount.");
    if (amt > availableBalance) return alert(`Amount cannot exceed available balance of ₹${availableBalance.toLocaleString("en-IN")}.`);
    if (!bankDetails.accountNumber.trim() || !bankDetails.ifsc.trim()) return alert("Please enter bank account details.");
    const finalChequeDataUrl = chequeFile ? null : (voidChequeData || null);
    if (!chequeFile && !finalChequeDataUrl) return alert("Please upload a photo of your cancelled cheque.");
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    const isEdit = initialData?.id;
    let fileDataUrl = null;
    if (chequeFile) {
      fileDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(chequeFile);
      });
    }
    await onSubmit({ 
      amount, 
      paymentMode: "bank", 
      mobileNumber: "", 
      bankDetails, 
      chequeFile, 
      chequeDataUrl: fileDataUrl,
      savedChequeUrl: chequeFile ? null : voidChequeData,
      _isEdit: isEdit, 
      _payoutId: initialData?.id 
    });
    setSubmitting(false);
    setSubmitted(true);
  }

  if (submitted) {
    const isEdit = initialData?.id;
    return (
      <div className="ProfilePayOverlay">
        <div className="ProfilePayModal" style={{ maxWidth: 420 }}>
          <div className="ProfileBillSuccessBody">
            <div className="ProfileBillSuccessIcon">✓</div>
            <h3>{isEdit ? "Payout Request Updated!" : "Payout Request Submitted!"}</h3>
            <p>Your payout request of <strong>₹{parseFloat(amount).toLocaleString("en-IN")}</strong> has been {isEdit ? "updated" : "submitted"}. Processing within 2–3 business days.</p>
            <div className="ProfileBillSuccessNote">📧 A confirmation has been sent to our team.</div>
            <button className="ProfileBtn ProfileBtnPrimary" style={{ marginTop: 20 }} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ProfilePayOverlay">
      <div className="ProfilePayModal ProfilePayModal--wide">
        <div className="ProfilePayModalHeader">
          <div className="ProfilePayModalTitle">
            <div className="ProfilePayModalFlag"><Icons.CreditCard /></div>
            <div>
              <div className="ProfilePayModalCountry">Request Payout</div>
              <div className="ProfilePayModalType">Available: ₹{availableBalance.toLocaleString("en-IN")}</div>
            </div>
          </div>
          <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
        </div>
        <div className="ProfilePayModalBody" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="ProfilePfField">
            <label>Payout Amount (₹) *</label>
            <input
              type="number"
              min="1"
              max={availableBalance}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Up to ₹${availableBalance.toLocaleString("en-IN")}`}
            />
          </div>

          {/* Bank Details — only option */}
          <div className="ProfilePayBankOnlyNotice">
            <Icons.Bank /> Payouts are processed via Bank Transfer only
          </div>

          <div className="ProfileBankDetailsGrid">
            <div className="ProfilePfField"><label>Account Number *</label><input value={bankDetails.accountNumber} onChange={(e) => setBankDetails((p) => ({ ...p, accountNumber: e.target.value }))} placeholder="1234567890" /></div>
            <div className="ProfilePfField"><label>IFSC Code *</label><input value={bankDetails.ifsc} onChange={(e) => setBankDetails((p) => ({ ...p, ifsc: e.target.value.toUpperCase() }))} placeholder="SBIN0001234" /></div>
            <div className="ProfilePfField"><label>MICR Code</label><input value={bankDetails.micr} onChange={(e) => setBankDetails((p) => ({ ...p, micr: e.target.value }))} placeholder="110002009" /></div>
            <div className="ProfilePfField"><label>Customer ID</label><input value={bankDetails.customerId} onChange={(e) => setBankDetails((p) => ({ ...p, customerId: e.target.value }))} placeholder="CIF / Customer ID" /></div>
          </div>

          {/* Cancelled Cheque Upload — required */}
          <div className="ProfilePfField">
            <label>Upload Cancelled Cheque Photo *</label>
            {voidChequeData && (
              <div className="ProfileChequeOptionBox">
                <div style={{ marginBottom: 12 }}>
                  <div 
                    className={`ProfileChequeOption ${!chequeFile ? "active" : ""}`}
                    onClick={() => setChequeFile(null)}
                  >
                    {/* <input
                      type="radio"
                      name="chequeOption"
                      checked={!chequeFile}
                      onChange={() => setChequeFile(null)}
                    /> */}
                    <span>Use saved Void Cheque from Documents</span>
                  </div>
                  
                  {!chequeFile && (
                    <div style={{ marginLeft: 32, marginTop: 10, display: "flex", gap: 8 }}>
                      {/*<button className="ProfileDocBtn" onClick={() => window.open(voidChequeData, "_blank")}><Icons.Documents /> View</button>*/}
                      <button className="ProfileDocBtn" onClick={() => handleDownloadCheque(voidChequeData)}><Icons.Download /> Download</button>
                    </div>
                  )}
                </div>
                <div>
                  <div 
                    className={`ProfileChequeOption ${chequeFile ? "active" : ""}`}
                    onClick={() => {
                      if (!chequeFile) {
                        chequeInputRef.current?.click();
                      }
                    }}
                  >
                    {/* <input
                      type="radio"
                      name="chequeOption"
                      checked={!!chequeFile}
                      onChange={() => {
                        if (!chequeFile) {
                          chequeInputRef.current?.click();
                        }
                      }}
                    /> */}
                    <span>Upload new cancelled cheque</span>
                  </div>
                  {chequeFile && (
                    <div style={{ marginLeft: 32, marginTop: 10, display: "flex", gap: 8 }}>
                      {/*<button className="ProfileDocBtn" onClick={() => window.open(URL.createObjectURL(chequeFile), "_blank")}><Icons.Documents /> View</button>*/}
                      <button className="ProfileDocBtn" onClick={() => handleDownloadCheque(URL.createObjectURL(chequeFile))}><Icons.Download /> Download</button>
                      <button className="ProfileDocBtn" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => setChequeFile(null)}>✕ Remove</button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!voidChequeData && (
              <div
                className="ProfileBillFileArea ProfileBillFileArea--cheque"
                onClick={() => chequeFile ? null : chequeInputRef.current?.click()}
              >
                {chequeFile
                  ? <><Icons.Check /> {chequeFile.name}</>
                  : <><Icons.Upload /> Click to upload cancelled cheque (JPG, PNG, PDF)</>}
              </div>
            )}
            {chequeFile && !voidChequeData && (
              <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                {/*<button className="ProfileDocBtn" onClick={() => window.open(URL.createObjectURL(chequeFile), "_blank")}><Icons.Documents /> View</button>*/}
                <button className="ProfileDocBtn" onClick={() => handleDownloadCheque(URL.createObjectURL(chequeFile))}><Icons.Download /> Download</button>
                <button className="ProfileDocBtn" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => setChequeFile(null)}>✕ Remove</button>
              </div>
            )}
            <input
              ref={chequeInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={(e) => setChequeFile(e.target.files?.[0] || null)}
            />
            {!voidChequeData && !chequeFile && (
              <div style={{ fontSize: 12, color: "var(--Profile-text-3)", marginTop: 6 }}>
                Please upload a clear photo of a cancelled cheque. This is required to verify your bank account before processing.
              </div>
            )}
            {voidChequeData && !chequeFile && (
              <div style={{ fontSize: 12, color: "var(--Profile-text-3)", marginTop: 6 }}>
                Using your saved Void Cheque. Select "Upload new" to use a different cheque.
              </div>
            )}
          </div>

          <div className="ProfilePayNote"><Icons.Info /> Payouts are processed within 2–3 business days after review.</div>
        </div>
        <div className="ProfilePayModalFooter">
          <button className="ProfileBtn ProfileBtnOutline" onClick={onClose}>Cancel</button>
          <button className="ProfileBtnPay" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="ProfileSpinner" /> Submitting…</> : <><Icons.CreditCard /> Submit Payout Request</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  REDEMPTION REQUEST CARDS
// ────────────────────────────────────────────
function PayoutCard({ request }) {
  const isPending = request.status === REDEMPTION_STATUS.PENDING;
  return (
    <div className="ProfileRedemptionCard">
      <div className="ProfileRedemptionCardTop">
        <div className="ProfileRedemptionCardLabel">Payout Request — ₹{(request.amount || 0).toLocaleString("en-IN")}</div>
        <span className={`ProfileStatusBadge ${isPending ? "ProfileStatusBadge--pending" : "ProfileStatusBadge--done"}`}>
          <span className="ProfileStatusDot" />{isPending ? "Pending" : "Completed"}
        </span>
      </div>
      <div className="ProfileRedemptionCardMeta">
        <span>Bank Transfer</span>
        <span>{formatDate(request.submittedAt)}</span>
      </div>
      {!isPending && <div className="ProfileRedemptionCardDeducted" style={{ color: "var(--Profile-green)", fontSize: 11.5, marginTop: 4 }}><Icons.Check /> Processed &amp; transferred</div>}
    </div>
  );
}

function RedemptionCard({ request }) {
  const isPending = request.status === REDEMPTION_STATUS.PENDING;
  return (
    <div className="ProfileRedemptionCard">
      <div className="ProfileRedemptionCardTop">
        <div className="ProfileRedemptionCardLabel">{request.offerName}</div>
        <span className={`ProfileStatusBadge ${isPending ? "ProfileStatusBadge--pending" : "ProfileStatusBadge--done"}`}>
          <span className="ProfileStatusDot" />{isPending ? "Pending" : "Completed"}
        </span>
      </div>
      <div className="ProfileRedemptionCardMeta">
        <span>₹{(request.amount || 0).toLocaleString("en-IN")} · Bank Transfer</span>
        <span>{formatDate(request.submittedAt)}</span>
      </div>
      {!isPending && <div className="ProfileRedemptionCardDeducted" style={{ color: "var(--Profile-green)", fontSize: 11.5, marginTop: 4 }}><Icons.Check /> Amount deducted &amp; processed</div>}
    </div>
  );
}

function VoucherRedemptionCard({ request }) {
  const isPending = request.status === REDEMPTION_STATUS.PENDING;
  return (
    <div className="ProfileRedemptionCard">
      <div className="ProfileRedemptionCardTop">
        <div className="ProfileRedemptionCardLabel">{request.brand} — {request.discount}</div>
        <span className={`ProfileStatusBadge ${isPending ? "ProfileStatusBadge--pending" : "ProfileStatusBadge--done"}`}>
          <span className="ProfileStatusDot" />{isPending ? "Pending" : "Completed"}
        </span>
      </div>
      <div className="ProfileRedemptionCardMeta">
        <span>{request.category}</span>
        <span>{formatDate(request.submittedAt)}</span>
      </div>
    </div>
  );
}

function RedemptionRequestsSection({ redemptionRequests, voucherRedemptions, payoutRequests, shopOrders, interviewTxns, generalTxns, role, userEmail, onEditPayout, onDeletePayout }) {
  const navigate = useNavigate();
  const [tab, setTab]                = useState("payouts");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [payTypeFilter, setPayTypeFilter] = useState("all");
  const [search, setSearch]          = useState("");
  const [filterMonth, setFilterMonth]      = useState("");
  const [filterYear, setFilterYear]        = useState("");
  const [showAllInterview, setShowAllInterview] = useState(false);
  const [showAllSchedule, setShowAllSchedule]   = useState(false);
  const [showAllEsim, setShowAllEsim]           = useState(false);
  const [showAllPassport, setShowAllPassport]   = useState(false);
  const [storedBookings, setStoredBookings] = useState([]);
  const [storedPassportBookings, setStoredPassportBookings] = useState([]);

  useEffect(() => {
    let mounted = true;
    if (!userEmail) {
      setStoredBookings([]);
      return () => { mounted = false; };
    }

    MeetingService.list()
      .then((res) => {
        if (!mounted) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map((m) => ({
          id: m.human_id || m.id,
          typeLabel: m.meetingTypeLabel || m.meetingType || m.type || "Meeting",
          totalAmount: Number(m.amount ?? m.totalAmount ?? m.fee ?? 0),
          rewardDeduction: Number(m.rewardUsed ?? m.rewardDeduction ?? 0),
          paymentMethod: m.paymentMethod || (Number(m.rewardUsed ?? m.rewardDeduction ?? 0) > 0 ? "Reward" : "Paid"),
          bookedAt: m.bookedAt || m.created_at || m.createdAt || m.updated_at || m.updatedAt,
          date: m.date || m.created_at || m.createdAt,
        }));
        setStoredBookings(mapped);
      })
      .catch(() => {
        if (mounted) setStoredBookings([]);
      });

    return () => { mounted = false; };
  }, [userEmail]);

  useEffect(() => {
    let mounted = true;
    if (!userEmail) {
      setStoredPassportBookings([]);
      return () => { mounted = false; };
    }

    PassportService.list(userEmail)
      .then((res) => {
        if (!mounted) return;
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const mapped = rows.map((p) => ({
          id: p.human_id || p.id,
          date: p.created_at || p.createdAt || p.updated_at || p.updatedAt,
          label: "Passport Application",
          amount: -(Math.abs(Number(p.totalAmount ?? p.amount ?? p.fee ?? 0))),
          totalAmount: Math.abs(Number(p.totalAmount ?? p.amount ?? p.fee ?? 0)),
          rewardUsed: Math.abs(Number(p.rewardUsed ?? p.rewardDeduction ?? 0)),
          paymentMethod: p.paymentMethod || (Number(p.rewardUsed ?? p.rewardDeduction ?? 0) > 0 ? "Reward" : "Paid"),
          note: p.note || "",
        }));
        setStoredPassportBookings(mapped);
      })
      .catch(() => {
        if (mounted) setStoredPassportBookings([]);
      });

    return () => { mounted = false; };
  }, [userEmail]);

  const handleDownloadCheque = (dataUrl) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "void_cheque";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const years = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));
  const months = [
    { v: "",   l: "All months" },
    { v: "01", l: "January" }, { v: "02", l: "February" }, { v: "03", l: "March" },
    { v: "04", l: "April" },   { v: "05", l: "May" },      { v: "06", l: "June" },
    { v: "07", l: "July" },    { v: "08", l: "August" },   { v: "09", l: "September" },
    { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" },
  ];

  function matchDate(dateStr) {
    if (!dateStr) return true;
    const d = dateStr.includes("T") ? dateStr : dateStr + "T00:00:00";
    const dt = new Date(d);
    if (filterYear  && String(dt.getFullYear())              !== filterYear)  return false;
    if (filterMonth && String(dt.getMonth() + 1).padStart(2, "0") !== filterMonth) return false;
    return true;
  }

  const q = search.toLowerCase();

  const filteredPayouts = payoutRequests.filter((r) =>
    matchDate(r.submittedAt) &&
    (!q || String(r.amount || "").includes(q) || (r.id || "").toLowerCase().includes(q))
  );
  const filteredBills = redemptionRequests.filter((r) =>
    matchDate(r.submittedAt) &&
    (!q || (r.offerName || "").toLowerCase().includes(q) || (r.category || "").toLowerCase().includes(q) || (r.id || "").toLowerCase().includes(q))
  );
  const filteredOrders = (shopOrders || []).filter((o) =>
    matchDate(o.date) &&
    (!q || (o.id || "").toLowerCase().includes(q) ||
      (o.items || []).some((it) => (it.name || "").toLowerCase().includes(q)) ||
      (o.status || "").toLowerCase().includes(q) ||
      (o.totalAmount || 0).toString().includes(q) ||
      (o.paymentMethod || "").toLowerCase().includes(q))
  );

  const pendingPayouts   = filteredPayouts.filter((r) => r.status === REDEMPTION_STATUS.PENDING);
  const completedPayouts = filteredPayouts.filter((r) => r.status === REDEMPTION_STATUS.COMPLETED);
  const pendingBills     = filteredBills.filter((r) => r.status === REDEMPTION_STATUS.PENDING);
  const completedBills   = filteredBills.filter((r) => r.status === REDEMPTION_STATUS.COMPLETED);

  const filteredInterviewTxns = (interviewTxns || []).filter((t) =>
    matchDate(t.date) &&
    (!q || t.label?.toLowerCase().includes(q) || t.note?.toLowerCase().includes(q) || String(t.amount).includes(q))
  );

  const filteredGeneralTxns = (generalTxns || []).filter((t) =>
    matchDate(t.date) &&
    (!q || t.label?.toLowerCase().includes(q) || t.note?.toLowerCase().includes(q) || String(t.amount).includes(q))
  );

  const ESIM_LABELS = ["esim", "esim purchase", "sim purchase"];
  const PASSPORT_LABELS = ["passport application", "passport re-application", "passport buddy", "passport"];

  // Extract schedule meeting transactions from stored bookings
  const mergedScheduleItems = useMemo(() => {
    const bookingItems = storedBookings
      .filter((b) => !!b.paymentMethod)
      .map((b) => {
        if (b.paymentMethod === "Pay by Reward") {
          return {
            id: b.id,
            label: "Schedule Meeting",
            typeLabel: b.typeLabel || "",
            amount: -(Math.abs(b.totalAmount || 0)),
            type: "debit",
            date: b.bookedAt || b.date,
            totalPrice: Math.abs(b.totalAmount || 0),
            rewardUsed: Math.abs(b.rewardDeduction || b.totalAmount || 0),
            paymentMethod: "Reward",
            note: "",
          };
        }
        return {
          id: b.id,
          label: "Schedule Meeting",
          typeLabel: b.typeLabel || "",
          amount: -(Math.abs(b.totalAmount || 0)),
          type: "debit",
          date: b.bookedAt || b.date,
          totalPrice: Math.abs(b.totalAmount || 0) + Math.abs(b.rewardDeduction || 0),
          rewardUsed: b.rewardDeduction || 0,
          paymentMethod: b.rewardDeduction > 0 && b.paymentMethod
            ? `Reward + ${b.paymentMethod}`
            : (b.paymentMethod || "Paid"),
          note: "",
        };
      });

    const all = [...bookingItems];
    all.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    return all.filter((t) =>
      matchDate(t.date) && (!q || t.label?.toLowerCase().includes(q) || t.note?.toLowerCase().includes(q) || String(t.amount).includes(q))
    );
  }, [storedBookings, q, filterMonth, filterYear]);

  const hasReward = (t) => {
    const method = (t.paymentMethod || "").toLowerCase();
    const note = (t.note || "").toLowerCase();
    return method.includes("reward") || t.rewardUsed > 0 || note.includes("reward");
  };

  const filteredEsimOrders = useMemo(() => {
    // 1. eSIM orders from the shopOrders prop
    const shopFromProp = filteredOrders.filter((o) =>
      ESIM_LABELS.some((lbl) =>
        (o.productName || "").toLowerCase().includes(lbl) ||
        (o.planName || "").toLowerCase().includes(lbl) ||
        (o.items || []).some((it) => (it.name || "").toLowerCase().includes(lbl))
      ) || o.country
    );
    // 2. Keep eSIM orders backend-sourced only (via `shopOrders` prop)
    const shopFromStorage = [];
    // 3. eSIM wallet transactions from generalTxns (passed via prop)
    //    Exclude "discount" labels (the full purchase already has a shop order)
    const walletFromProp = (generalTxns || [])
      .filter((t) =>
        ESIM_LABELS.some((lbl) => (t.label || "").toLowerCase().includes(lbl)) &&
        !(t.label || "").toLowerCase().includes("discount")
      )
      .map((t) => ({
        ...t,
        totalPrice: Math.abs(t.amount || 0),
        rewardUsed: Math.abs(t.amount || 0),
        paymentMethod: t.paymentMethod || "Reward",
        _fromWallet: true,
      }));
    // 4. Keep wallet fallback empty to avoid local/session-derived duplicates.
    const walletFromSource = [];
    // 5. Merge all sources with dedup by id
    const mergedMap = new Map();
    [...shopFromProp, ...shopFromStorage, ...walletFromProp, ...walletFromSource].forEach((item) => {
      if (item.id && !mergedMap.has(item.id)) mergedMap.set(item.id, item);
    });
    const merged = Array.from(mergedMap.values());
    merged.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    return payTypeFilter === "all" ? merged
      : payTypeFilter === "reward" ? merged.filter(hasReward)
      : merged.filter((t) => !hasReward(t));
  }, [filteredOrders, generalTxns, payTypeFilter, userEmail]);

  const filteredPassportTxns = useMemo(() => {
    const hasBookings = storedPassportBookings.length > 0;
    let items;
    if (hasBookings) {
      const bookingItems = storedPassportBookings
        .filter((b) => b.paymentMethod)
        .map((b) => ({
          id: b.id,
          label: b.type === "passport-buddy" ? "Passport Buddy" : b.type === "passport-reapplication" ? "Passport Re-Application" : "Passport Application",
          amount: -(Math.abs(b.totalAmount || 0)),
          type: "debit",
          date: b.bookedAt,
        totalPrice: b.paymentMethod === "Pay by Reward"
          ? Math.abs(b.totalAmount || 0)
          : Math.abs(b.totalAmount || 0) + Math.abs(b.rewardDeduction || 0),
          rewardUsed: b.paymentMethod === "Pay by Reward" ? (b.totalAmount || 0) : (b.rewardDeduction || 0),
          paymentMethod: b.rewardDeduction > 0 && b.paymentMethod && b.paymentMethod !== "Pay by Reward"
            ? `Reward + ${b.paymentMethod}`
            : (b.paymentMethod === "Pay by Reward" ? "Reward" : (b.paymentMethod || "Paid")),
          note: "",
        }));
      items = bookingItems;
    } else {
      items = (generalTxns || [])
        .filter((t) =>
          PASSPORT_LABELS.some((lbl) => (t.label || "").toLowerCase().includes(lbl))
        )
        .map((t) => {
          const rewardUsed = t.rewardUsed || Math.abs(t.amount || 0);
          return {
            ...t,
            rewardUsed,
            totalPrice: t.totalPrice || Math.abs(t.amount || 0),
            paymentMethod: t.paymentMethod || (rewardUsed > 0 ? "Reward" : "Paid"),
          };
        });
    }
    const filtered = items.filter((t) =>
      matchDate(t.date) && (!q || t.label?.toLowerCase().includes(q) || t.note?.toLowerCase().includes(q) || String(t.amount).includes(q))
    );
    return payTypeFilter === "all" ? filtered
      : payTypeFilter === "reward" ? filtered.filter(hasReward)
      : filtered.filter((t) => !hasReward(t));
  }, [generalTxns, storedPassportBookings, payTypeFilter, q, filterMonth, filterYear]);

  return (
    <div className="ProfileRedemptionSection">
      <div className="ProfileTxnSectionTitle" style={{ marginBottom: 12 }}>History</div>

      <div className="ProfileRedemptionTabRow">
        <button className={`ProfileAppFilterBtn ${tab === "payouts" ? "active" : ""}`} onClick={() => setTab("payouts")}>
          Payouts <span className="ProfileAppFilterCount">{filteredPayouts.length}</span>
        </button>
        <button className={`ProfileAppFilterBtn ${tab === "payments" ? "active" : ""}`} onClick={() => setTab("payments")}>
          Payments <span className="ProfileAppFilterCount">{filteredInterviewTxns.length + filteredGeneralTxns.length + filteredPassportTxns.length}</span>
        </button>
      </div>

      <div className="ProfileHistSearchRow">
        <div className="ProfileHistSearchBox">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order ID, item, status, amount, payment…"
          />
          {search && <button className="ProfileSearchClearBtn" onClick={() => setSearch("")}>✕</button>}
        </div>
        <select className="ProfileHistDateSelect" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
          {months.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select className="ProfileHistDateSelect" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {tab === "payouts" && (
        filteredPayouts.length === 0 ? (
          <div className="ProfileTxnEmpty">{payoutRequests.length === 0 ? "No payout requests yet." : "No results for current filters."}</div>
        ) : (
          <>
            {pendingPayouts.length > 0 && (
              <><div className="ProfileRedemptionSubTitle">Pending</div>
              {pendingPayouts.map((r) => (
                <div key={r.id} className="ProfileRedemptionCard">
                  <div className="ProfileRedemptionCardTop">
                    <div className="ProfileRedemptionCardLabel">Payout Request — ₹{(r.amount || 0).toLocaleString("en-IN")}</div>
                    <span className="ProfileStatusBadge ProfileStatusBadge--pending"><span className="ProfileStatusDot" />Pending</span>
                  </div>
                  <div className="ProfileRedemptionCardMeta">
                    <span>Bank Transfer</span>
                    <span style={{ fontWeight: 600 }}>{formatDate(r.submittedAt)}</span>
                  </div>
                  {r.chequeDataUrl && (
                    <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                      {/*<button className="ProfileDocBtn" onClick={() => window.open(r.chequeDataUrl, "_blank")}><Icons.Documents /> View Cheque</button>
                      <button className="ProfileDocBtn" onClick={() => handleDownloadCheque(r.chequeDataUrl)}><Icons.Download /> Download</button>*/}
                    </div>
                  )}
                  <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="ProfileHistReceiptBtn" onClick={() => downloadPayoutReceipt(r)}> Download Receipt</button>
                 
                  </div>
                </div>
              ))}</>
            )}
            {completedPayouts.length > 0 && (
              <><div className="ProfileRedemptionSubTitle">Completed</div>
              {completedPayouts.map((r) => (
                <div key={r.id} className="ProfileRedemptionCard">
                  <div className="ProfileRedemptionCardTop">
                    <div className="ProfileRedemptionCardLabel">Payout Request — ₹{(r.amount || 0).toLocaleString("en-IN")}</div>
                    <span className="ProfileStatusBadge ProfileStatusBadge--done"><span className="ProfileStatusDot" />Completed</span>
                  </div>
                  <div className="ProfileRedemptionCardMeta">
                    <span>Bank Transfer</span>
                    <span style={{ fontWeight: 600 }}>{formatDate(r.submittedAt)}</span>
                  </div>
                  <div style={{ color: "var(--Profile-green)", fontSize: 11.5, marginTop: 4 }}><Icons.Check /> Processed &amp; transferred</div>
                  <div style={{ marginTop: 8 }}>
                    <button className="ProfileHistReceiptBtn" onClick={() => downloadPayoutReceipt(r)}>📅 Download Receipt (PDF)</button>
                  </div>
                </div>
              ))}</>
            )}
          </>
        )
      )}

      {/* COMMENTED FOR FUTURE USE - Bill Uploads section
      {tab === "bills" && (
        filteredBills.length === 0 ? (
          <div className="ProfileTxnEmpty">{redemptionRequests.length === 0 ? "No bill uploads yet. Upload bills from the Bill Upload tab." : "No results for current filters."}</div>
        ) : (
          <>
            {pendingBills.length > 0 && (
              <><div className="ProfileRedemptionSubTitle">Pending</div>
              {pendingBills.map((r) => (
                <div key={r.id} className="ProfileRedemptionCard">
                  <div className="ProfileRedemptionCardTop">
                    <div className="ProfileRedemptionCardLabel">{r.offerName || "Bill Upload"}</div>
                    <span className="ProfileStatusBadge ProfileStatusBadge--pending"><span className="ProfileStatusDot" />Pending</span>
                  </div>
                  <div className="ProfileRedemptionCardMeta">
                    <span>₹{(r.amount || 0).toLocaleString("en-IN")} · {r.category || ""}</span>
                    <span style={{ fontWeight: 600 }}>{formatDate(r.submittedAt)}</span>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <button className="ProfileHistReceiptBtn" onClick={() => downloadBillReceipt(r)}>📅 Download Receipt (PDF)</button>
                  </div>
                </div>
              ))}</>
            )}
            {completedBills.length > 0 && (
              <><div className="ProfileRedemptionSubTitle">Completed</div>
              {completedBills.map((r) => (
                <div key={r.id} className="ProfileRedemptionCard">
                  <div className="ProfileRedemptionCardTop">
                    <div className="ProfileRedemptionCardLabel">{r.offerName || "Bill Upload"}</div>
                    <span className="ProfileStatusBadge ProfileStatusBadge--done"><span className="ProfileStatusDot" />Completed</span>
                  </div>
                  <div className="ProfileRedemptionCardMeta">
                    <span>₹{(r.amount || 0).toLocaleString("en-IN")} · {r.category || ""}</span>
                    <span style={{ fontWeight: 600 }}>{formatDate(r.submittedAt)}</span>
                  </div>
                  <div style={{ color: "var(--Profile-green)", fontSize: 11.5, marginTop: 4 }}><Icons.Check /> Amount deducted &amp; processed</div>
                  <div style={{ marginTop: 8 }}>
                    <button className="ProfileHistReceiptBtn" onClick={() => downloadBillReceipt(r)}>📅 Download Receipt (PDF)</button>
                  </div>
                </div>
              ))}</>
            )}
          </>
        )
      )}
      */}

      {tab === "payments" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <select className="ProfileHistDateSelect" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} style={{ flex: 1, minWidth: 160 }}>
              <option value="all">All</option>
              <option value="interview">AI Interview</option>
              <option value="schedule-meeting">Schedule Meeting</option>
              <option value="passport">Passport</option>
              <option value="esim">eSIMs</option>
            </select>
            <select className="ProfileHistDateSelect" value={payTypeFilter} onChange={(e) => setPayTypeFilter(e.target.value)} style={{ flex: 1, minWidth: 120 }}>
              <option value="all">All</option>
              <option value="reward">Pay by Reward</option>
              <option value="money">Pay by Money</option>
            </select>
          </div>
          {(paymentFilter === "all" || paymentFilter === "interview") && (
            <>
              {(() => {
                const filtered = payTypeFilter === "all" ? filteredInterviewTxns
                  : payTypeFilter === "reward" ? filteredInterviewTxns.filter(hasReward)
                  : filteredInterviewTxns.filter((t) => !hasReward(t));
                const showMore = filtered.length > 4;
                const displayed = showAllInterview ? filtered : filtered.slice(0, 4);
                return filtered.length > 0 ? (
                <>
                   <div className="ProfileAllSectionHeader">
                    {paymentFilter === "all" && <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--Profile-font-display)" }}>AI Interview</div>}
                    <div className="ProfileAllSectionBtns" style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                      <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/interview")}>
                        <Icons.Search /> <span className="ProfileBtnLabel" style={{ color: "#fff" }}>Explore Interviews</span>
                      </button>
                      <button
                        className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                        onClick={() => generateInterviewPdfReport(filtered, userEmail, payTypeFilter !== "money")}
                      >
                        <Icons.Download /> <span className="ProfileBtnLabel">Download PDF Report</span>
                      </button>
                    </div>
                  </div>
                  <div className="ProfileRedemptionList">
                    {displayed.map((txn, idx) => (
                      <div key={txn.id || idx} className="ProfileRedemptionCard">
                        <div className="ProfileRedemptionCardTop">
                          <div className="ProfileRedemptionCardLabel">
                            {txn.label || "AI Interview Payment"}
                          </div>
                          <span className="ProfileStatusBadge ProfileStatusBadge--done">
                            <span className="ProfileStatusDot" />
                            Paid
                          </span>
                        </div>
                        <div className="ProfileRedemptionCardMeta">
                          <span style={{ color: "#000" }}>
                            Total: ₹{roundAmount(txn.totalPrice || (txn.amount + (txn.actualPaid || 0))).toLocaleString("en-IN")}
                          </span>
                          <span style={{ fontWeight: 600 }}>{txn.date ? new Date(txn.date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : ""}</span>
                        </div>
                      <div style={{ fontSize: 11.5, color: "var(--Profile-text-3)", marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                        {(txn.rewardUsed || txn.amount) > 0 && (
                          <span style={{ color: "var(--Profile-red)" }}>− Reward Deducted: <strong>₹{roundAmount(txn.rewardUsed || txn.amount || 0).toLocaleString("en-IN")}</strong></span>
                        )}
                        {txn.paymentMethod && (
                          <span>Payment Mode: <strong>{(txn.rewardUsed || txn.amount) > 0 && txn.paymentMethod !== "Reward" && !txn.paymentMethod.startsWith("Reward") ? "Reward + " + txn.paymentMethod : txn.paymentMethod}</strong></span>
                        )}
                      </div>
                      </div>
                    ))}
                  </div>
                  {showMore && (
                    <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                      <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" onClick={() => setShowAllInterview(!showAllInterview)}>
                        {showAllInterview ? "Show Less" : "View More"}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="ProfileTxnEmpty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <span>No AI Interview payments yet.</span>
                  <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/interview")}>
                    Explore Interviews
                  </button>
                </div>
              )}
              )()}
            </>
          )}

          {(paymentFilter === "all" || paymentFilter === "schedule-meeting") && (
            <>
              {(() => {
                const filtered = payTypeFilter === "all" ? mergedScheduleItems
                  : payTypeFilter === "reward" ? mergedScheduleItems.filter(hasReward)
                  : mergedScheduleItems.filter((t) => !hasReward(t));
                const showMore = filtered.length > 4;
                const displayed = showAllSchedule ? filtered : filtered.slice(0, 4);
                return filtered.length > 0 ? (
                <>
                   <div className="ProfileAllSectionHeader">
                    {paymentFilter === "all" && <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--Profile-font-display)" }}>Schedule Meeting</div>}
                    <div className="ProfileAllSectionBtns" style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                      <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/schedule-meeting")}>
                        <Icons.Search /> <span className="ProfileBtnLabel" style={{ color: "#fff" }}>Explore Schedule Meeting</span>
                      </button>
                      <button
                        className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                        onClick={() => generateSchedulePdfReport(filtered, userEmail, "Schedule Meeting Payments", payTypeFilter !== "money")}
                      >
                        <Icons.Download /> <span className="ProfileBtnLabel">Download PDF Report</span>
                      </button>
                    </div>
                  </div>
                  <div className="ProfileRedemptionList">
                  {displayed.map((txn, idx) => (
                    <div key={txn.id || idx} className="ProfileRedemptionCard">
                      <div className="ProfileRedemptionCardTop">
                          <div className="ProfileRedemptionCardLabel">
                            Schedule Meeting{txn.typeLabel ? ` — ${txn.typeLabel}` : ""}
                          </div>
                        <span className="ProfileStatusBadge ProfileStatusBadge--done">
                          <span className="ProfileStatusDot" />
                          Paid
                        </span>
                      </div>
                      <div className="ProfileRedemptionCardMeta">
                        <span style={{ color: "#000" }}>
                          Total: ₹{Math.abs(txn.totalPrice || txn.amount || 0).toLocaleString("en-IN")}
                        </span>
                        <span style={{ fontWeight: 600 }}>{txn.date ? new Date(txn.date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : ""}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--Profile-text-3)", marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                        {(txn.rewardUsed || 0) > 0 && (
                          <span style={{ color: "var(--Profile-red)" }}>− Reward Deducted: <strong>₹{roundAmount(txn.rewardUsed || 0).toLocaleString("en-IN")}</strong></span>
                        )}
                        {txn.paymentMethod && (
                          <span>Payment Mode: <strong>{txn.paymentMethod}</strong></span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {showMore && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                    <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" onClick={() => setShowAllSchedule(!showAllSchedule)}>
                      {showAllSchedule ? "Show Less" : "View More"}
                    </button>
                  </div>
                )}
              </>
              ) : (
                <div className="ProfileTxnEmpty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <span>No Schedule Meeting payments yet.</span>
                  <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/schedule-meeting")}>
                    Explore Schedule Meeting
                  </button>
                </div>
              )}
              )()}
            </>
          )}

          {(paymentFilter === "all" || paymentFilter === "passport") && (
            <>
              {(() => {
                const filtered = filteredPassportTxns;
                const showMore = filtered.length > 4;
                const displayed = showAllPassport ? filtered : filtered.slice(0, 4);
                return filtered.length > 0 ? (
                <>
                   <div className="ProfileAllSectionHeader">
                    {paymentFilter === "all" && <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--Profile-font-display)" }}>Passport</div>}
                    <div className="ProfileAllSectionBtns" style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                      <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/Passportguide")}>
                        <Icons.Search /> <span className="ProfileBtnLabel" style={{ color: "#fff" }}>Explore Passport</span>
                      </button>
                      <button
                        className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                        onClick={() => generatePassportPdfReport(filtered, userEmail, "Passport Payments", payTypeFilter !== "money")}
                      >
                        <Icons.Download /> <span className="ProfileBtnLabel">Download PDF Report</span>
                      </button>
                    </div>
                  </div>
                  <div className="ProfileRedemptionList">
                  {displayed.map((txn, idx) => (
                    <div key={txn.id || idx} className="ProfileRedemptionCard">
                      <div className="ProfileRedemptionCardTop">
                          <div className="ProfileRedemptionCardLabel">
                            {txn.label || "Passport Payment"}
                          </div>
                        <span className="ProfileStatusBadge ProfileStatusBadge--done">
                          <span className="ProfileStatusDot" />
                          Paid
                        </span>
                      </div>
                      <div className="ProfileRedemptionCardMeta">
                        <span style={{ color: "#000" }}>
                          Total: ₹{Math.abs(txn.totalPrice || txn.amount || 0).toLocaleString("en-IN")}
                        </span>
                        <span style={{ fontWeight: 600 }}>{txn.date ? new Date(txn.date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : ""}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--Profile-text-3)", marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                        {(txn.rewardUsed || 0) > 0 && (
                          <span style={{ color: "var(--Profile-red)" }}>− Reward Deducted: <strong>₹{roundAmount(txn.rewardUsed || 0).toLocaleString("en-IN")}</strong></span>
                        )}
                        {txn.paymentMethod && (
                          <span>Payment Mode: <strong>{txn.paymentMethod}</strong></span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {showMore && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                    <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" onClick={() => setShowAllPassport(!showAllPassport)}>
                      {showAllPassport ? "Show Less" : "View More"}
                    </button>
                  </div>
                )}
              </>
              ) : (
                <div className="ProfileTxnEmpty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <span>No Passport payments yet.</span>
                  <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/Passportguide")}>
                    Explore Passport
                  </button>
                </div>
              )}
              )()}
            </>
          )}

          {(paymentFilter === "all" || paymentFilter === "esim") && (
            <>
              {(() => {
                const showMore = filteredEsimOrders.length > 4;
                const displayed = showAllEsim ? filteredEsimOrders : filteredEsimOrders.slice(0, 4);
                return (
                <>
                {filteredEsimOrders.length > 0 && (
                  <>
                   <div className="ProfileAllSectionHeader">
                    {paymentFilter === "all" && <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--Profile-font-display)" }}>eSIMs</div>}
                    <div className="ProfileAllSectionBtns" style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                      <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/Esim")}>
                        <Icons.Search /> <span className="ProfileBtnLabel" style={{ color: "#fff" }}>Explore eSIMs</span>
                      </button>
                      <button
                        className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                        onClick={() => generateEsimPdfReport(filteredEsimOrders, userEmail, payTypeFilter !== "money")}
                      >
                        <Icons.Download /> <span className="ProfileBtnLabel">Download PDF Report</span>
                      </button>
                    </div>
                  </div>
                  <div className="ProfileRedemptionList" style={{ marginTop: 0 }}>
                    {displayed.map((order, idx) => (
                      <div key={order.id || idx} className="ProfileRedemptionCard">
                        <div className="ProfileRedemptionCardTop">
                          <div className="ProfileRedemptionCardLabel">
                            eSIM Purchase{order.country ? ` — ${order.country}` : ""}{order.productName ? ` ${order.productName.replace("eSIM - ", "")}` : ""}
                          </div>
                          <span className="ProfileStatusBadge ProfileStatusBadge--done">
                            <span className="ProfileStatusDot" />Paid
                          </span>
                        </div>
                        <div className="ProfileRedemptionCardMeta">
                          <span style={{ color: "#000" }}>
                            Total: ₹{roundAmount(order.totalPrice || order.total || order.amount || 0).toLocaleString("en-IN")}
                          </span>
                          <span style={{ fontWeight: 600 }}>{order.date ? new Date(order.date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : ""}</span>
                        </div>

                        <div style={{ fontSize: 11.5, color: "var(--Profile-text-3)", marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                          {order.rewardUsed > 0 && (
                            <span style={{ color: "var(--Profile-red)" }}>− Reward Deducted: <strong>₹{roundAmount(order.rewardUsed || 0).toLocaleString("en-IN")}</strong></span>
                          )}
                          {order.paymentMethod && (
                            <span>Payment Mode: <strong>{order.paymentMethod}</strong></span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {showMore && (
                    <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                      <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover" onClick={() => setShowAllEsim(!showAllEsim)}>
                        {showAllEsim ? "Show Less" : "View More"}
                      </button>
                    </div>
                  )}
                </>)}
                {filteredEsimOrders.length === 0 && (
                  <div className="ProfileTxnEmpty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: 12 }}>
                    <span>No eSIMs purchases yet.</span>
                    <button className="ProfileBtn ProfileBtnPrimary ProfileBtnSm" onClick={() => navigate("/Esim")}>
                      Explore eSIMs
                    </button>
                  </div>
                )}
                </>
              )})()}
            </>
          )}
          {paymentFilter === "all" && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
              <button
                className="ProfileBtn ProfileBtnOutline ProfileBtnSm ProfileBtnNoHover"
                onClick={() => {
                  const interviewData = payTypeFilter === "all" ? filteredInterviewTxns
                    : payTypeFilter === "reward" ? filteredInterviewTxns.filter(hasReward)
                    : filteredInterviewTxns.filter((t) => !hasReward(t));
                  const scheduleData = payTypeFilter === "all" ? mergedScheduleItems
                    : payTypeFilter === "reward" ? mergedScheduleItems.filter(hasReward)
                    : mergedScheduleItems.filter((t) => !hasReward(t));
                  const passportData = payTypeFilter === "all" ? filteredPassportTxns
                    : payTypeFilter === "reward" ? filteredPassportTxns.filter(hasReward)
                    : filteredPassportTxns.filter((t) => !hasReward(t));
                  generateAllPdfReport(interviewData, scheduleData, passportData, filteredEsimOrders, userEmail, payTypeFilter !== "money");
                }}
              >
                <Icons.Download /> Download PDF Report (All)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
//  REWARDS TAB — default export
// ────────────────────────────────────────────
export default function RewardsTab({
  wallet,
  user,
  commissionPct,
  referralCode,
  redemptionRequests,
  voucherRedemptions,
  payoutRequests,
  milestoneData,
  onSubmitBill,
  onVoucherRedeem,
  onPayoutRequest,
}) {
  const [activeWallet, setActiveWallet] = useState("redeemable");
  const [voucherCatFilter, setVoucherCatFilter] = useState("All");
  const [billModalVoucher, setBillModalVoucher] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [redeemVoucher, setRedeemVoucher] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [editPayout, setEditPayout] = useState(null);
  const [shopOrders, setShopOrders] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  // Extract AI Interview transactions from wallet for Payout History - merge duplicate entries
  const interviewTxns = useMemo(() => {
    const allTxns = [
      ...(wallet?.redeemable?.transactions || []),
      ...(wallet?.nonRedeemable?.transactions || [])
    ].filter((t) => (t.label || "").toLowerCase().includes("ai interview"));

    // Group by sessionId or matching ID timestamp to merge split transactions
    const grouped = {};

    allTxns.forEach(t => {
      // Get grouping key - use sessionId if available, otherwise extract base from ID
      let key;
      if (t.sessionId) {
        key = t.sessionId;
      } else {
        // Extract base ID by removing _r or _nr suffix
        key = (t.id || "").replace(/_r$|_nr$/, '');
      }

      if (!grouped[key]) {
        grouped[key] = { ...t };
      } else {
        // Merge amounts into single transaction
        grouped[key].amount = (grouped[key].amount || 0) + (t.amount || 0);
      }
    });

    return Object.values(grouped);
  }, [wallet?.redeemable?.transactions, wallet?.nonRedeemable?.transactions]);

  const generalTxns = useMemo(() => {
    const allTxns = [
      ...(wallet?.redeemable?.transactions || []),
      ...(wallet?.nonRedeemable?.transactions || [])
    ].filter((t) =>
      !(t.label || "").toLowerCase().includes("ai interview") &&
      !t.isMilestone &&
      !(t.label || "").toLowerCase().includes("welcome bonus")
    );
    return allTxns;
  }, [wallet?.redeemable?.transactions, wallet?.nonRedeemable?.transactions]);

  useEffect(() => {
    if (user?.email) getShopOrders(user.email).then(setShopOrders);
  }, [user?.email]);

  useEffect(() => {
    let mounted = true;
    RedemptionService.listVouchers()
      .then((res) => {
        if (!mounted) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map((v) => ({
          id: v.id,
          brand: v.brand || "Voucher",
          category: v.category || "General",
          logo: "🎁",
          color: "#1d4ed8",
          bgColor: "#eff6ff",
          discount: v.discountLabel || v.discount_label || "",
          description: v.description || "",
          code: v.voucherCode || v.voucher_code || "",
          expiry: v.expiryDate || v.expiry_date ? formatDate(v.expiryDate || v.expiry_date) : "",
          allowBillUpload: !!v.allowBillUpload,
        }));
        setVouchers(mapped);
      })
      .catch(() => {
        if (mounted) setVouchers([]);
      });
    return () => { mounted = false; };
  }, []);

  const nonBal = wallet?.nonRedeemable?.balance ?? 0;
  const reBal = wallet?.redeemable?.balance ?? 0;
  const rawNonTxns = wallet?.nonRedeemable?.transactions ?? [];
  const rawReTxns = wallet?.redeemable?.transactions ?? [];
  const isScheduleMeetingTxn = (t) => (t.label || "").toLowerCase().includes("schedule meeting");
  const nonTxns = rawNonTxns.filter((t) => !isScheduleMeetingTxn(t));
  const reTxns = rawReTxns.filter((t) => !isScheduleMeetingTxn(t));
  const role = user?.role || "individual";
  const roundCustom = (num) => {
    const decimal = num % 1;
    return decimal > 0.5 ? Math.ceil(num) : Math.floor(num);
  };

  // Commission and discount rates for this role
  const commRate = `${((COMMISSION_RATES[role] || 0) * 100).toFixed(0)}%`;
  const discountRate = `${((REFERRAL_DISCOUNT_RATES[role] || 0) * 100).toFixed(0)}%`;

  // Bill Upload: shown for all roles (individual, member, agent) - COMMENTED FOR FUTURE USE
  const showBillUpload = false;

  const voucherSource = vouchers;
  const filteredVouchers = voucherCatFilter === "All"
    ? voucherSource
    : voucherSource.filter((v) => v.category === voucherCatFilter);

  function handleUploadBill(voucher) {
    setBillModalVoucher(voucher);
    setShowBillModal(true);
  }

  async function handleBillSubmit(formData) {
    await onSubmitBill({ ...formData, category: billModalVoucher?.category || "Shopping" });
  }

  function handleEditPayout(payout) {
    setEditPayout(payout);
    setShowPayoutModal(true);
  }

  async function handleDeletePayout(payoutId) {
    if (!user?.email) return;
    const updated = await deletePayoutRequest(user.email, payoutId);
    onPayoutRequest({ _updateList: updated });
  }

  async function handlePayoutSubmitWithEdit(data) {
    if (editPayout?.id) {
      await onPayoutRequest({ ...data, _isEdit: true, _payoutId: editPayout.id });
    } else {
      await onPayoutRequest(data);
    }
    setShowPayoutModal(false);
    setEditPayout(null);
  }

  // Build wallet toggle tabs based on role
  // const walletTabs = [
  //   { key: "non-redeemable", label: "Non-Redeemable" },
  //   { key: "redeemable", label: "Redeemable" },
  //   ...(showBillUpload ? [{ key: "bill-upload", label: "Bill Upload" }] : []),
  //   { key: "redeem-history", label: "Redeem History" },
  // ];
  // COMMENTED FOR FUTURE USE - Bill Upload section
  const walletTabs = [
    { key: "non-redeemable", label: "Non-Redeemable" },
    { key: "redeemable", label: "Redeemable" },
    { key: "redeem-history", label: "Payout History" },
  ];

  return (
    <div className="ProfileRewardsPage">
      {/* Modals */}
      {showBillModal && (
        <BillUploadModal
          voucher={billModalVoucher}
          onClose={() => setShowBillModal(false)}
          onSubmit={async (data) => { await handleBillSubmit(data); setShowBillModal(false); }}
        />
      )}
      {redeemVoucher && (
        <VoucherRedeemModal
          voucher={redeemVoucher}
          onClose={() => setRedeemVoucher(null)}
          onConfirm={async (v) => { await onVoucherRedeem(v); setRedeemVoucher(null); }}
        />
      )}
      {showPayoutModal && (
        <PayoutRequestModal
          availableBalance={reBal}
          onClose={() => { setShowPayoutModal(false); setEditPayout(null); }}
          onSubmit={handlePayoutSubmitWithEdit}
          initialData={editPayout}
          userEmail={user?.email}
        />
      )}

      {/* Wallet Toggle */}
      <div className="ProfileWalletToggle" data-aos="fade-up" data-aos-delay="50">
        {walletTabs.map((tab, i) => (
          <button
            key={tab.key}
            className={`ProfileWalletToggleBtn ${activeWallet === tab.key ? "active" : ""}`}
            onClick={() => setActiveWallet(tab.key)}
            data-aos="fade-up"
            data-aos-delay={50 + i * 30}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* NON-REDEEMABLE */}
      {activeWallet === "non-redeemable" && (
        <div className="ProfileWalletPanel">
          <div className="ProfileWalletBalanceCard ProfileWalletBalanceCard--non" data-aos="fade-up" data-aos-delay="50">
            <div className="ProfileWbcEyebrow">Non-Redeemable Balance</div>
            <div className="ProfileWbcAmount">₹{roundCustom(nonBal).toLocaleString("en-IN")}</div>
            <div className="ProfileWbcDesc">
             Submit your Documents To use the Discount.
            </div>
            <div className="ProfileWbcRule">
              <span className="ProfileWbcRuleIcon"><Icons.Info /></span>
              Minimum service fee of <strong>₹{MIN_SVC_FEE_REDEMPTION}</strong> required to apply discount
            </div>
          </div>
          
          {/* <div className="ProfileWelcomeBonusCard" data-aos="fade-up" data-aos-delay="100">
            <div className="ProfileWbcardLeft">
              <div className="ProfileWbcardIcon"><Icons.Gift /></div>
              <div>
                <div className="ProfileWbcardTitle">Welcome Bonus</div>
                 <div className="ProfileWbcardSub">₹{WELCOME_BONUS} credited on joining — use it as discount on your next application</div> 
              </div>
            </div>
            <div className="ProfileWbcardBadge">₹{WELCOME_BONUS}</div>
          </div> */}
          <TransactionHistorySection
            transactions={nonTxns}
            sectionTitle="Transaction History"
            totalLabel="Total Credited"
            userName={user?.name || ""}
            reportLabel="Non-Redeemable Wallet"
            availableBalance={nonBal}
            showPayoutBar={false}
          />
        </div>
      )}

      {/* REDEEMABLE */}
      {activeWallet === "redeemable" && (
        <div className="ProfileWalletPanel">
          <div className="ProfileWalletBalanceCard ProfileWalletBalanceCard--re" data-aos="fade-up" data-aos-delay="50">
            <div className="ProfileWbcEyebrow">Redeemable Balance</div>
            <div className="ProfileWbcAmount">₹{roundCustom(reBal).toLocaleString("en-IN")}</div>
            <div className="ProfileWbcDesc">
              {role === "individual"
                ? "Earn milestone rewards on your service fee payments. Upgrade to Member to also earn referral commissions."
                : "Earn commission when others use your referral code, plus milestone rewards. Freely redeemable for payouts."}
            </div>
          </div>

          {/* Referral Code — locked for individual, visible for member/agent */}
          <ReferralCodeCard code={referralCode} role={role} data-aos="fade-up" data-aos-delay="100" />

          {/* Commission rate card — shown for member and agent only */}
          {role !== "individual" && (
            <div className={`ProfileCommissionCard ProfileCommissionCard--${role}`} data-aos="fade-up" data-aos-delay="150">
              <div className="ProfileCommissionCardLeft">
                <div className="ProfileCommissionCardIcon">
                  {role === "agent" ? <Icons.Shield /> : <Icons.Star />}
                </div>
                <div>
                  <div className="ProfileCommissionCardRole">
                    {role.charAt(0).toUpperCase() + role.slice(1)} Commission Rate
                  </div>
                  <div className="ProfileCommissionCardDesc">
                    {role === "member" && `As a Member, you earn ${commRate} commission when someone else pays using your referral code. They receive a ${discountRate} discount on their service fee.`}
                    {role === "agent"  && `As an Agent, you earn ${commRate} commission — the maximum rate — when someone else pays using your referral code. They receive a ${discountRate} discount on their service fee.`}
                  </div>
                </div>
              </div>
              <div className="ProfileCommissionCardPct">{commRate}</div>
            </div>
          )}

          {/* How referral works — member and agent only */}
          {role !== "individual" && (
            <div className="ProfileCommissionHow" data-aos="fade-up" data-aos-delay="200">
              <div className="ProfileCommissionHowTitle">How Referral Commission Works</div>
              <div className="ProfileCommissionHowSteps">
                <div className="ProfileChs" data-aos="fade-up" data-aos-delay="250"><div className="ProfileChsNum">1</div><div className="ProfileChsText">Complete your first paid application to unlock your unique referral code.</div></div>
                <div className="ProfileChs" data-aos="fade-up" data-aos-delay="300"><div className="ProfileChsNum">2</div><div className="ProfileChsText">Share your code with friends, clients, or on social media.</div></div>
                <div className="ProfileChs" data-aos="fade-up" data-aos-delay="350"><div className="ProfileChsNum">3</div><div className="ProfileChsText">When <strong>someone else</strong> pays using your code, they get a <strong>{discountRate} discount</strong> on the service fee — and you earn <strong>{commRate}</strong> of their final service fee here.</div></div>
                <div className="ProfileChs" data-aos="fade-up" data-aos-delay="400"><div className="ProfileChsNum">4</div><div className="ProfileChsText">Request payout — funds transferred to your bank account.</div></div>
              </div>
            </div>
          )}

          {/* Upgrade nudge for individual */}
          {role === "individual" && (
            <div className="ProfileUpgradeNudge" data-aos="fade-up" data-aos-delay="200">
              <div className="ProfileUpgradeNudgeIcon"><Icons.TrendingUp /></div>
              <div>
                <div className="ProfileUpgradeNudgeTitle">Unlock Referral Code — Upgrade to Member</div>
                <div className="ProfileUpgradeNudgeSub">
                  Individual accounts do not have referral codes. Upgrade to Member (10% commission, 10% code discount for users) or Agent (20% commission, 20% discount) to get your referral code and multiply your earnings.
                </div>
              </div>
            </div>
          )}

          {/* Upgrade nudge for member */}
          {role === "member" && (
            <div className="ProfileUpgradeNudge" data-aos="fade-up" data-aos-delay="200">
              <div className="ProfileUpgradeNudgeIcon"><Icons.TrendingUp /></div>
              <div>
                <div className="ProfileUpgradeNudgeTitle">Earn Even More as an Agent</div>
                <div className="ProfileUpgradeNudgeSub">
                  Upgrade to Agent to earn 20% commission on every referral (up from 10%), give code-users a 20% discount, and earn 20% milestone rewards on your own applications.
                </div>
              </div>
            </div>
          )}

          {/* Milestone progress section */}
          <MilestoneSection milestoneData={milestoneData} role={role} data-aos="fade-up" data-aos-delay="250" />

          {/* Combined Earnings history + always-visible payout bar */}
          <RedeemableHistoryPanel
            reTxns={reTxns}
            role={role}
            userName={user?.name || ""}
            availableBalance={reBal}
            onRequestPayout={() => setShowPayoutModal(true)}
          />
        </div>
      )}

      {/* BILL UPLOAD — individual and member only */}
      {activeWallet === "bill-upload" && showBillUpload && (
        <div className="ProfileWalletPanel">
          <p className="ProfileBillUploadDesc">
            Upload bills from any category to earn cashback rewards into your redeemable balance.
          </p>
          <div className="ProfileBillUploadGrid">
            {[
              { icon: "🍽️", label: "Food & Dining",    desc: "Restaurants, cafes, delivery" },
              { icon: "🛍️", label: "Shopping",         desc: "Retail, online, apparel" },
              { icon: "✈️",   label: "Travel",           desc: "Hotels, flights, cabs" },
              { icon: "🏥", label: "Health & Wellness", desc: "Medical, pharmacy, gym" },
              { icon: "🎦", label: "Entertainment",    desc: "Movies, events, gaming" },
              { icon: "📚", label: "Education",         desc: "Courses, books, stationery" },
              { icon: "🔧", label: "Services",          desc: "Utilities, repairs, home" },
              { icon: "🏠", label: "Home & Living",     desc: "Furniture, groceries, decor" },
            ].map((cat) => (
              <div
                key={cat.label}
                className="ProfileBillUploadCard"
                onClick={() => handleUploadBill({ category: cat.label, icon: cat.icon })}
              >
                <div className="ProfileBillUploadIcon">{cat.icon}</div>
                <div className="ProfileBillUploadLabel">{cat.label}</div>
                <div className="ProfileBillUploadSub">{cat.desc}</div>
                <button className="ProfileBillUploadBtn">Upload Bill</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REDEEM HISTORY */}
      {activeWallet === "redeem-history" && (
        <div className="ProfileWalletPanel">
          <RedemptionRequestsSection
            redemptionRequests={redemptionRequests}
            voucherRedemptions={voucherRedemptions}
            payoutRequests={payoutRequests}
            shopOrders={shopOrders}
            interviewTxns={interviewTxns}
            generalTxns={generalTxns}
            role={role}
            userEmail={user?.email}
            onEditPayout={handleEditPayout}
            onDeletePayout={handleDeletePayout}
          />
        </div>
      )}
    </div>
  );
}

